<template>
  <div
    v-if="isDraftLoaded"
    id="editor-page"
    :aria-label="editorAriaLabel"
    role="region"
    tabindex="0"
    @wheel="onWheel"
  >
    <div
      class="editor editor-theme"
      ref="editor"
      :class="[
        editorThemeClasses,
        {
          'editor--is-fullscreen': editorIsFullscreen,
          'editor--is-fullscreen-board': editorIsFullscreen && !isSlidesLayout,
          'editor--is-fullscreen-slides': editorIsFullscreen && isSlidesLayout,
          'editor--toolbar': showEditorToolbar,
          'editor--module-rotating': moduleIsRotating,
          'editor--is-modal-active': currentModalCanvasId,
        },
      ]"
      :style="{
        background,
        height: editorIsFullscreen ? undefined : (height ?? updatedEmbeddedHeight ?? embeddedEditorHeight),
        minHeight: editorIsFullscreen ? undefined : minHeight,
      }"
    >
      <AppMessageManager v-if="editorIsFullscreen" />
      <div
        v-if="mode === 'EDIT'"
        aria-live="polite"
        class="nebula-screenreader-only"
      >
        <div v-if="editorViewModePreview">
          {{ $t('Preview On') }}
        </div>
        <div v-else>
          {{ $t('Preview Off') }}
        </div>
        <div v-if="editorSnapGrid">
          {{ $t('Grid On') }}
        </div>
        <div v-else>
          {{ $t('Grid Off') }}
        </div>
      </div>
      <div
        ref="fullscreenImmersiveReader"
        class="editor__fullscreen-immersive-reader"
        :class="{
          'editor__fullscreen-immersive-reader--visible': fullscreenImmersiveReaderVisible,
        }"
      >
        <LoadingIndicator
          v-if="fullscreenImmersiveReaderLoading"
          absolute
        />
      </div>
      <EditorStatusMessage />
      <div
        v-if="!notesOpen && !customizationOpen && !addModuleOpen && !editorIsFullscreen"
        class="editor__drawer-buttons"
      >
        <NebulaTooltip
          v-if="showAddModule"
          class="editor__drawer-button-wrapper"
          :position="isRtl ? 'left' : 'right'"
          :text="$t('Select to Add')"
        >
          <button
            class="comet-button comet-button--primary comet-button--icon-only
              editor__drawer-button editor__drawer-button--add"
            :class="isAssetSlide && 'comet-button--disabled'"
            de-tracking-name="studio_left-side-options_select-to-add-button"
            ref="module"
            aria-expanded="false"
            :aria-label="$t('Select to Add')"
            :disabled="isAssetSlide"
            @click="toggleDrawer('addModule')"
          >
            <NebulaIcon
              symbol-id="add"
              class-override="comet-button__icon comet-button__icon--left"
            />
          </button>
        </NebulaTooltip>
        <NebulaTooltip
          v-if="showCustomizationDrawer"
          class="editor__drawer-button-wrapper"
          :position="isRtl ? 'left' : 'right'"
          :text="$t('Customization')"
        >
          <button
            class="comet-button comet-button--primary comet-button--icon-only
              editor__drawer-button editor__drawer-button--customization"
            ref="customization"
            aria-expanded="false"
            :aria-label="$t('Customization Drawer')"
            @click="toggleDrawer('customization')"
          >
            <NebulaIcon
              symbol-id="palette"
              class-override="comet-button__icon comet-button__icon--left"
            />
          </button>
        </NebulaTooltip>
        <NebulaTooltip
          v-if="showNotesCanvas"
          class="editor__drawer-button-wrapper"
          :position="isRtl ? 'left' : 'right'"
          :text="$t('Teacher Notes')"
        >
          <button
            class="comet-button comet-button--primary comet-button--icon-only
              editor__drawer-button editor__drawer-button--notes"
            ref="notes"
            aria-expanded="false"
            :aria-label="$t('Teacher Notes')"
            de-tracking-name="studio_left-side-options_notes-toggle_close"
            @click="toggleDrawer('notes')"
          >
            <NebulaIcon
              symbol-id="teacher-note"
              class-override="comet-button__icon comet-button__icon--left"
            />
          </button>
        </NebulaTooltip>
      </div>

      <!--
        Note: All these drawers use v-show instead of v-if to make sure
        that the transitions happen correctly. Using v-if will rerender the wrapper
        div when it gets toggled, which swallows the css transition (see STUD-801).
      -->
      <div v-show="showAddModule">
        <AddModuleDrawer
          :scale="currentModalCanvasId ? modalScaleZoomed : scaleZoomed"
          :canvas-name="currentModalCanvasId ? 'modalModules' : 'modules'"
          :always-open="!!currentModalCanvasId"
        />
      </div>
      <template v-if="mode === 'EDIT'">
        <div
          v-show="showCustomizationDrawer"
          class="customization-drawer-wrapper"
        >
          <CustomizationDrawer
            @focus-open="focusOpen($event)"
          />
        </div>
      </template>
      <div
        v-if="notesVisible"
        v-show="showNotesCanvas"
        class="notes__contents"
        :class="{ 'notes__contents--open': notesOpen }"
        de-tracking-name="studio_left-side-options_notes-toggle_open"
        @click="setActiveCanvas('notes')"
      >
        <NotesCanvas
          ref="notesCanvas"
          v-show="notesOpen"
          class="grid-canvas"
          :class="{
            'editor-drawer--icon': editorIsFullscreen && !useWideNotes,
            'editor-drawer--no-animation': !notesDrawerAnimated,
          }"
          :mode="canvasMode"
          :key="mode"
          :notes-open="notesOpen"
          :menu-container="editorRef"
          :module-animations-enabled="moduleAnimationsEnabled && canvasMode === 'VIEW'"
          :use-wide-notes="useWideNotes"
          :page-id="pageId"
        />
        <transition
          name="add-module__container-transition"
          enter-from-class="add-module__container-transition--enter-from"
          enter-active-class="add-module__container-transition--enter-active"
          leave-active-class="add-module__container-transition--leave-active"
          leave-to-class="add-module__container-transition--leave-to"
        >
          <div
            v-show="notesOpen"
            :aria-hidden="(!notesOpen).toString()"
            class="add-module__container"
          >
            <!-- v-show required here so AddModule doesn't lose
              its reference to the notes canvas -->
            <AddModule
              v-show="showAddModule"
              canvas-name="notes"
              class="add-module--notes"
              :de-tracking-name="'studio_teacher-notes-options_add-button'"
              theme="notes"
            />
          </div>
        </transition>
      </div>
      <EditorPageDrawerSidebar
        v-if="pageDrawer"
        v-show="!currentModalCanvasId && !editorIsFullscreen"
        v-bind="pageDrawer"
      />
      <div
        v-if="showNavigation()"
      >
        <button
          v-if="isMysteryTheme && editorIsFullscreen"
          :aria-label="$t('Exit full screen mode')"
          class="nebula-button
            nebula-button--icon-only editor__close-fullscreen check"
          @click="onExitFullscreen"
        >
          <NebulaIcon
            symbol-id="x"
            size="xxl"
          />
        </button>
      </div>
      <!--
        This key is used to prevent the editorStage ref inside from getting refreshed,
        since it's used by the ResizeObserver
      -->
      <div
        class="editor__stage-outer-wrap"
        :class="{ 'editor__stage-outer-wrap--mystery':
          editorIsFullscreen
          && isMysteryTheme
          && showMysteryControls,
        }"
        key="editor__stage-outer-wrap"
        @click="setActiveCanvas('modules')"
        ref="stageContainer"
      >
        <div
          class="editor__invalid-placement-message"
          v-if="isSlidesLayout && invalidPlacementAttempted"
        >
          <CometBlockMessage
            block-message-type="error"
            role="alert"
          >
            <div v-if="invalidPlacementAssetData && invalidPlacementAssetData.length">
              <p>{{ $t('There was no room for these assets:') }}</p>
              <ul class="comet-block-message__list">
                <li
                  v-for="asset in invalidPlacementAssetData"
                  :key="asset.id"
                >
                  {{ asset.title }}
                </li>
              </ul>
            </div>
            <p v-else>
              {{ $t("There is no room on this slide for this module.") }}
            </p>
          </CometBlockMessage>
        </div>
        <!-- These floating buttons are used when embedded in
          /learn/player for public boards -->
        <EditorFloatingButtons
          v-if="useFloatingActionButtons"
          :mode="mode"
          :show-immersive-reader="false"
          :horizontal-canvas-padding="horizontalCanvasPadding"
          :vertical-canvas-padding="verticalCanvasPadding"
        />
        <div
          ref="editorStage"
          class="editor__stage"
          :class="{ 'editor__stage--landscape-only-fullscreen': isMobileOrTablet() }"
          @scroll="onScroll"
        >
          <div
            v-if="noPagesVisibleFullscreen"
            class="editor__no-pages-visible-fullscreen-message"
          >
            <comet-block-message :show-icon="false">
              {{ isSlidesLayout
                ? $t('All slides are hidden and cannot be presented')
                : $t('All pages are hidden and cannot be presented')
              }}
            </comet-block-message>
          </div>
          <template v-else>
            <div
              class="editor__rotate-screen-message"
              @touchstart.stop
              @touchmove.stop
            >
              <button
                :aria-label="$t('Exit full screen mode')"
                class="nebula-button nebula-button--m
                  nebula-button--icon-only editor__close-rotate-message"
                @click="onExitFullscreen"
              >
                <NebulaIcon
                  symbol-id="x"
                  size="m"
                />
              </button>
              <div class="editor__rotate-screen-image">
                <div class="editor__rotate-screen-image-spacer" />
              </div>
              {{ $t('Please rotate your device.') }}
            </div>
            <BoardNavigationOverlay
              v-if="editorIsFullscreen && !isMysteryTheme"
              :show-immersive-reader="showImmersiveReaderFullscreen"
              :show-notes="notesVisible"
              :show-highlight-notes="enableTextSelectTools && highlights"
              :theme-override="themeOverride"
              :submit-assignment-button="submitAssignmentButton"
              class="editor__fullscreen-header"
              @touchstart.stop
              @touchmove.stop
              @immersive-reader-clicked="launchImmersiveReaderFullscreen"
            />
            <div
              v-if="showNavigation()"
              class="
                editor__stage-sticky
                editor__stage-sticky--block-center
              "
            >
              <NavigationArrow
                action="prev"
                :container="$refs.editorStage"
                :mode="mode"
                :type="isMysteryTheme && showMysteryControls ? 'mystery' : navigationType"
              />
            </div>
            <div
              class="
                editor__stage-sticky
                editor__stage-sticky--block-start-3x
                editor__stage-sticky--inline-end-3x
              "
              v-if="showPageNumber && pages && editorRef && !editorIsFullscreen
                && !userIsStudent && !studentView"
            >
              <PageNumberNotification
                :parent-container="editorRef"
                movement-tracking-enabled
              />
            </div>
            <div
              v-if="showZoomContentHeader"
              class="editor__zoom-content-header"
              :style="zoomContentHeaderStyle"
            >
              <EditorCanvasBadges
                :canvas-scale="scaleZoomed"
                :badges="canvasBadges"
              />
            </div>
            <div
              :style="zoomSpacerStyle"
              class="editor__zoom-spacer"
            />
            <NebulaButton
              v-if="showPreviousSlideButton"
              :style="zoomPreviousButtonStyle"
              class="
                editor__slide-button
                editor__slide-button--prev
                editor__zoom-slide-button
              "
              :class="{
                [`editor__slide-button--${slideButtonType}`]: true,
                'editor__slide-button--rounded': roundNavigationButtons,
              }"
              de-tracking-name="studio_canvas_previous-slide-button"
              icon-left="caret-left"
              :size="slideButtonSize"
              :type="slideButtonType === 'rectangle' ? 'flat' : 'pill'"
              :aria-label="isSlidesLayout ? $t('Previous Slide') : $t('Previous Page')"
              :text="slideButtonType === 'rectangle' ? $t('Prev') : null"
              :is-disabled="prevPageDisabled"
              @click="goToPrevPage"
            />
            <div
              ref="zoomContent"
              class="editor__zoom-content"
              :class="{
                'editor__zoom-content--transition': enableZoomTransition,
                'editor__zoom-content--border': canvasBorderWidth,
                'editor__zoom-content--view-mode': mode === 'VIEW'
                  || isThumbnailGenerator || editorViewModePreview,
                'editor__zoom-content--mystery-science': isMysteryTheme,
              }"
              :style="zoomContentStyle"
            >
              <FullSlideOverlay
                v-if="isMysteryFullSlideOverlay()"
                :key="`full-slide-overlay-${currentPage.id}`"
                :page="currentPage"
              />
              <!--
                The key for the editor canvas carousel forces a reload if we toggle
                between EDIT and VIEW mode, or between grid and no-grid, or if a
                different draft is loaded.
              -->
              <EditorCanvasCarousel
                ref="editorCanvasCarousel"
                :mode="canvasMode"
                :key="`${draftId}--${mode}--${gridIsDisabled ? 'no-grid' : 'grid'}`"
                :scroll-container="editorScrollContainer"
                :scale="editorIsFullscreen ? scale : scaleZoomed"
                :page-id="pageId"
                :menu-container="editorRef"
                :snap-grid="!currentModalCanvasId ? snapGrid : undefined"
                :module-animations-enabled="moduleAnimationsEnabled && canvasMode === 'VIEW'"
                :show-mystery-controls="showMysteryControls"
                :theme-override="themeOverride"
                @height-updated="onLayoutHeightUpdated"
              />
            </div>
            <TextSelectTools
              v-if="textSelectToolsKey"
              class="editor__text-select-tools"
              :context-container-selector="textSelectContextContainer"
              :node-selectors="nodeSelectors"
              :metadata="highlightMetaData"
              :init-data="highlightInitData"
              :api-origin="apiUrl"
              :api-version="2"
              :page-data="{}"
              :buttonYellowAttributes="{
                'de-tracking-name': 'studio_text-select-mini-menu_yellow-highlight-button',
              }"
              :buttonPinkAttributes="{
                'de-tracking-name': 'studio_text-select-mini-menu_pink-highlight-button',
              }"
              :buttonGreenAttributes="{
                'de-tracking-name': 'studio_text-select-mini-menu_green-highlight-button',
              }"
              :buttonBlueAttributes="{
                'de-tracking-name': 'studio_text-select-mini-menu_blue-highlight-button',
              }"
              :buttonPlayAttributes="{
                'de-tracking-name': 'studio_text-select-mini-menu_play-text-button',
              }"
              :buttonAddNoteAttributes="{
                'de-tracking-name': 'studio_text-select-mini-menu_add-note-button',
              }"
              :buttonRemoveNoteAttributes="{
                'de-tracking-name': 'studio_text-select-mini-menu_remove-note-button',
              }"
              ref="textSelect"
              @create-note="onCreateHighlightNote"
              @show-note="onShowHighlightNote"
              @highlights-retrieved="onHighlightsRetrieved"
              @highlight-created="onHighlightCreated"
              @highlight-updated="onHighlightUpdated"
              @highlight-deleted="onHighlightDeleted"
              @pointer-coordinates-reset="onTextSelectCoordsReset"
              :key="textSelectToolsKey"
              :use-intersection-observer="false"
            />
            <NebulaButton
              v-if="showNextSlideButton"
              :style="zoomNextButtonStyle"
              class="
                editor__slide-button
                editor__slide-button--next
                editor__zoom-slide-button
              "
              :class="{
                [`editor__slide-button--${slideButtonType}`]: true,
                'editor__slide-button--rounded': roundNavigationButtons,
              }"
              de-tracking-name="studio_canvas_next-slide-button"
              icon-left="caret-right"
              :size="slideButtonSize"
              :type="slideButtonType === 'rectangle' ? 'flat' : 'pill'"
              :aria-label="isSlidesLayout ? $t('Next Slide') : $t('Next Page')"
              :text="slideButtonType === 'rectangle' ? $t('Next') : null"
              :is-disabled="nextPageDisabled"
              @click="goToNextPage"
            />
            <div
              v-if="showNavigation()"
              class="
                editor__stage-sticky
                editor__stage-sticky--block-center
                editor__stage-sticky--inline-end
              "
            >
              <NavigationArrow
                action="next"
                :container="$refs.editorStage"
                :mode="mode"
                :type="isMysteryTheme && showMysteryControls ? 'mystery' : navigationType"
              />
            </div>
          </template>

          <template v-if="currentModalCanvasId">
            <!--
              The modal overlay is included here since it has to
              be a direct child of the element it's sticky within
            -->
            <div
              class="editor__modal-overlay"
            />
            <ModalCanvas
              class="grid-canvas"
              :mode="canvasMode"
              :key="mode"
              :scroll-container="editorScrollContainer"
              :zoom-level="modalZoomLevel"
              :menu-container="editorRef"
              :show-snap-grid="!!snapGrid"
              expand-height-to-max-rows
              :module-animations-enabled="moduleAnimationsEnabled && canvasMode === 'VIEW'"
              :page-id="pageId"
              ref="modalCanvas"
              @scale-zoomed-changed="modalScaleZoomed = $event"
            />
          </template>
        </div>
        <div
          v-if="showEditorToolbar"
          class="editor__toolbar"
        >
          <div class="editor__toolbar-left">
            <NebulaTooltip
              v-if="showUndoRedoButtons"
              class="editor__toolbar-item"
              :text="$t('Undo')"
            >
              <NebulaButton
                :aria-label="$t('Undo')"
                :is-disabled="!canUndo"
                class="editor__toolbar-button"
                de-tracking-name="studio_bottom-toolbar_undo-button"
                size="s"
                icon-left="undo"
                @click="undo"
              />
            </NebulaTooltip>
            <NebulaTooltip
              v-if="showUndoRedoButtons"
              class="editor__toolbar-item"
              :text="$t('Redo')"
            >
              <NebulaButton
                :aria-label="$t('Redo')"
                :is-disabled="!canRedo"
                class="editor__toolbar-button"
                de-tracking-name="studio_bottom-toolbar_redo-button"
                size="s"
                icon-left="redo"
                @click="redo"
              />
            </NebulaTooltip>
          </div>
          <div class="editor__toolbar-center" />
          <div class="editor__toolbar-right">
            <NebulaTitleTooltip
              v-if="allowSnapGrid"
              class="editor__toolbar-item"
              :text="editorSnapGrid ? $t('On (Ctrl + G)') : $t('Off (Ctrl + G)')"
            >
              <NebulaSwitch
                is-compressed
                is-inverted
                :is-disabled="canvasMode !== 'EDIT'"
                :label-text="$t('Grid')"
              >
                <template #checkbox>
                  <input
                    class="nebula-switch__input"
                    :de-tracking-name="editorSnapGrid
                      ? 'studio_bottom-toolbar_grid-toggle_on'
                      : 'studio_bottom-toolbar_grid-toggle_off'"
                    type="checkbox"
                    @input="toggleSnapGrid"
                    :checked="editorSnapGrid"
                    :disabled="canvasMode !== 'EDIT'"
                  >
                </template>
              </NebulaSwitch>
            </NebulaTitleTooltip>
            <div
              v-if="allowSnapGrid"
              class="editor__toolbar-divider"
            />
            <NebulaTitleTooltip
              v-if="mode === 'EDIT'"
              class="editor__toolbar-item"
              :text="editorViewModePreview ? $t('On (Ctrl + Space)') : $t('Off (Ctrl + Space)')"
            >
              <NebulaSwitch
                is-compressed
                is-inverted
                :label-text="$t('Preview')"
              >
                <template #checkbox>
                  <input
                    class="nebula-switch__input"
                    :de-tracking-name="editorViewModePreview
                      ? 'studio_bottom-toolbar_preview-toggle_on'
                      : 'studio_bottom-toolbar_preview-toggle_off'"
                    type="checkbox"
                    @input="toggleViewModePreview"
                    :checked="editorViewModePreview"
                  >
                </template>
              </NebulaSwitch>
            </NebulaTitleTooltip>
            <div
              v-if="mode === 'EDIT'"
              class="editor__toolbar-divider"
            />
            <div class="editor__toolbar-item">
              <ZoomControl
                ref="zoom"
                :value="currentModalCanvasId ? modalZoomLevel : zoomLevel"
                :min="10"
                :max="200"
                :step="5"
                :de-tracking-name-zoom-in="'studio_bottom-toolbar_zoom-in-button'"
                :de-tracking-name-zoom-out="'studio_bottom-toolbar_zoom-out-button'"
                :de-tracking-name-slider="'studio_bottom-toolbar_zoom-slider'"
                @input="onZoomChanged"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showChatDrawer"
        class="chat-drawer-wrapper"
      >
        <ChatDrawer
          de-tracking-name="studio_right-side-options_chat-toggle_open"
          @focus-open="focusOpen($event)"
        />
      </div>
      <div
        v-if="showChatDrawer && !chatOpen"
        class="editor__drawer-buttons"
      >
        <NebulaTooltip
          class="editor__drawer-button-wrapper editor__drawer-button-wrapper--right"
          :position="isRtl ? 'right' : 'left'"
          :text="$t('Chat')"
        >
          <button
            class="comet-button comet-button--primary comet-button--icon-only
              editor__drawer-button editor__drawer-button--right editor__drawer-button--chat"
            de-tracking-name="studio_right-side-options_chat-toggle_close"
            ref="chat"
            aria-expanded="false"
            :aria-label="$t('Chat')"
            @click="toggleDrawer('chat')"
          >
            <NebulaIcon
              symbol-id="live-chat-available"
              class-override="comet-button__icon comet-button__icon--left"
            />
          </button>
        </NebulaTooltip>
      </div>
      <transition
        name="editor__immersive-reader-transition"
        enter-from-class="editor__immersive-reader-transition--enter-from"
        enter-active-class="editor__immersive-reader-transition--enter-active"
        leave-active-class="editor__immersive-readertransition--leave-active"
        leave-to-class="editor__immersive-reader-transition--leave-to"
      >
        <ImmersiveReader
          class="editor__immersive-reader"
          :class="{ 'editor__immersive-reader--student-view': studentView }"
          v-if="showImmersiveReader && !useFloatingActionButtons"
          :mode="mode"
          :de-tracking-name="'studio_right-side-options_immersive-reader'"
          :parent-container="editorRef"
          :movement-tracking-enabled="!!editorRef && immersiveReaderOnHover && !isMobileOrTablet()"
          :tracked-keys="['Tab', 9]"
          @launch="immersiveReaderOpen = true"
          @exit="immersiveReaderOpen = false"
        />
      </transition>
      <HighlightNotesDrawer
        v-if="enableTextSelectTools"
        class="editor__highlight-notes-drawer"
        :text-select-meta-data="textSelectMetaData"
        :offset-bottom="showEditorToolbar ? '30px' : '0px'"
      />
      <ModalManager v-if="editorIsFullscreen" />
    </div>
  </div>
  <LoadingIndicator
    v-else
    absolute
  />
</template>

<script>
import '@discoveryedu/titan-text-editor/dist/titan-text-editor-assets/index.css';
import '@discoveryedu/vue-grid-layout/dist/style.css';
import '../components/modules/styles/proxima-nova-soft.css';
import '../components/modules/styles/agrandir.css';

import { defineAsyncComponent } from 'vue';
import {
  NebulaIcon,
  NebulaTooltip,
  NebulaButton,
  NebulaSwitch,
  NebulaTitleTooltip,
} from '@discoveryedu/nebula-components';
import { TextSelectTools } from '@discoveryedu/platform-components';
import { tokens as cometTokens } from '@discoveryedu/comet-core/data/tokens.json';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import fscreen from 'fscreen';
import { mapActions, mapState } from 'pinia';
import {
  every, get, isNumber, throttle, pick, first, forEach,
} from 'lodash-es';
import {
  EDITOR_CANVAS_WIDTH,
  EDITOR_CANVAS_HEIGHT,
  EDITOR_CANVAS_PADDING,
  EDITOR_CANVAS_PADDING_MOBILE_VERTICAL,
  EDITOR_CANVAS_PADDING_MOBILE_VERTICAL_WITH_HEADER,
  EDITOR_CANVAS_PADDING_HORIZONTAL_CIRCLE_BUTTON,
  EDITOR_CANVAS_PADDING_HORIZONTAL_CIRCLE_BUTTON_SMALL,
  EDITOR_CANVAS_PADDING_VERTICAL_WITH_HEADER,
  EDITOR_CANVAS_MIN_LAYOUT_HEIGHT,
  EDITOR_SNAP_GRID_CELL_WIDTH,
  EDITOR_SNAP_GRID_CELL_HEIGHT,
  EDITOR_SNAP_GRID_CELL_LARGE_WIDTH,
  EDITOR_SNAP_GRID_CELL_LARGE_HEIGHT,
  EDITOR_ZOOM_CONTENT_HEADER_HEIGHT,
  PRODUCT_SETTING_STUDIO_CHAT_ENABLED,
  PRODUCT_SETTING_MAZE_TRACKING_ENABLED,
} from '@/lib/constants';
import * as types from '@/lib/constants/store';
import AddModule from '@/components/AddModule.vue';
import CometBlockMessage from '@/components/ui-elements/CometBlockMessage.vue';
import AddModuleDrawer from '@/components/AddModuleDrawer.vue';
import EditorCanvasCarousel from '@/components/EditorCanvasCarousel.vue';
import EditorFloatingButtons from '@/components/EditorFloatingButtons.vue';
import EditorStatusMessage from '@/components/EditorStatusMessage.vue';
import HighlightNotesDrawer from '@/components/HighlightNotesDrawer.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import ModalCanvas from '@/components/ModalCanvas.vue';
import ModalManager from '@/components/ModalManager.vue';
import NotesCanvas from '@/components/NotesCanvas.vue';
import ImmersiveReader from '@/components/ImmersiveReader.vue';
import AppMessageManager from '@/components/messages/AppMessageManager.vue';
import immersiveReaderMixin from '@/mixins/immersiveReader';
import ZoomControl from '@/components/ZoomControl.vue';
import BoardNavigationOverlay from '@/components/navigation/BoardNavigationOverlay.vue';
import NavigationArrow from '@/components/navigation/NavigationArrow.vue';
import EditorPageDrawerSidebar from '@/components/EditorPageDrawerSidebar.vue';
import EditorCanvasBadges from '@/components/EditorCanvasBadges.vue';
import { bus } from '@/lib/eventBus';
import { getLocalData } from '@/lib/localData';
import { isTextInput, isMobileOrTablet } from '@/lib/utils';
import { getDomains } from '@/lib/api';
import { DraftIsDeletedError } from '@/lib/errors';
import FullSlideOverlay from '@/components/full-slide-asset/FullSlideOverlay.vue';
import PageNumberNotification from '@/components/navigation/PageNumberNotification.vue';

import {
  useAppStore,
  useAssessmentStore,
  useConfirmationStore,
  useEditorStore,
  useMessageStore,
  useModalStore,
  useTextSelectStore,
  useTrackingStore,
  useUserStore,
  useErrorStore,
} from '@/stores';

const ChatDrawer = defineAsyncComponent(() => import(/* webpackChunkName: "edit-mode" */ '@/components/ChatDrawer.vue'));
const CustomizationDrawer = defineAsyncComponent(() => import(/* webpackChunkName: "edit-mode" */ '@/components/CustomizationDrawer.vue'));

export default {
  name: 'StudioEditor',
  components: {
    FullSlideOverlay,
    AddModuleDrawer,
    AppMessageManager,
    BoardNavigationOverlay,
    ChatDrawer,
    EditorCanvasBadges,
    EditorCanvasCarousel,
    EditorFloatingButtons,
    EditorStatusMessage,
    EditorPageDrawerSidebar,
    HighlightNotesDrawer,
    NavigationArrow,
    AddModule,
    CometBlockMessage,
    CustomizationDrawer,
    ImmersiveReader,
    LoadingIndicator,
    ModalCanvas,
    ModalManager,
    NebulaIcon,
    NebulaTooltip,
    NebulaButton,
    NotesCanvas,
    ZoomControl,
    TextSelectTools,
    NebulaSwitch,
    NebulaTitleTooltip,
    PageNumberNotification,
  },
  mixins: [immersiveReaderMixin],
  data() {
    return {
      editorStageWidth: null,
      editorScrollContainer: null,
      editorRef: null,
      canvasResizeObserver: null,
      layoutHeight: 0,
      roundNavigationButtons: false,
      scale: 1,
      suppressRouteChangeEvents: false,
      activityTimeout: null,
      immersiveReaderOpen: false,
      zoomLevel: this.zoomPercentage,
      modalZoomLevel: 100,
      modalScaleZoomed: 1,
      scrollPercentTop: 0,
      scrollPercentLeft: 0,
      suppressScrollEvent: false,
      zoomContentStyle: null,
      zoomContentHeaderStyle: null,
      zoomSpacerStyle: null,
      zoomNextButtonStyle: null,
      zoomPreviousButtonStyle: null,
      enableZoomTransition: false,
      invalidPlacementAttempted: false,
      invalidPlacementAssetData: [],
      isMobileOrTablet,
      notesDrawerAnimated: true,
      fullscreenImmersiveReaderVisible: false,
      fullscreenImmersiveReaderLoading: false,
      moduleIsRotating: false,
      nebulaSpace1x: parseInt(nebulaTokens.space['1x'], 10),
      mobileLandscape: parseInt(nebulaTokens.breakpoints['mobile-landscape'], 10),
      textSelectToolsKey: null,
      textSelectMenuTrigger: null,
      updatedEmbeddedHeight: null,
    };
  },
  props: {
    showPageNumber: {
      type: Boolean,
      default: false,
    },
    navigationType: {
      type: String,
      default: 'default',
      validator(value) {
        return ['default', 'bottom', 'edge', 'mystery'].includes(value);
      },
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'VIEW',
    },
    useImmersiveReader: {
      type: Boolean,
      default: true,
    },
    immersiveReaderOnHover: {
      type: Boolean,
      default: false,
    },
    // these props below only used in the embedded view
    height: {
      type: [String, Number],
      default: null,
    },
    minHeight: {
      type: String,
      default: null,
    },
    background: {
      type: String,
      default: null,
    },
    canvasPadding: {
      type: [Number, Object],
      default: null,
    },
    canvasBorderWidth: {
      type: Number,
      default: 0,
    },
    canvasShadow: {
      type: String,
      default: null,
    },
    canvasBorderColor: {
      type: String,
      default: cometTokens.color.neutral['80'],
    },
    canvasBorderStyle: {
      type: String,
      default: 'solid',
    },
    loadDraft: {
      type: Boolean,
      default: true,
    },
    pageId: {
      type: String,
      default: null,
    },
    toolbarVisible: {
      type: Boolean,
      default: true,
    },
    slideButtonsVisible: {
      type: Boolean,
      default: true,
    },
    slideButtonsVisibleDisabled: {
      type: Boolean,
      default: false,
    },
    slideButtonType: {
      type: String,
      default: 'rectangle', // rectangle, circle
    },
    /**
     * Shows the page drawer inside the editor with badges.
     * e.g. { isAssigned: true, lessonName: 'Lesson 1' }
     */
    pageDrawer: {
      type: Object,
      default: null,
    },
    /**
     * Show/hide badges on top of the canvas.
     * e.g. { page: true, video: false, audio: false, question: true }
     */
    canvasBadges: {
      type: Object,
      default: null,
    },
    notesVisible: {
      type: Boolean,
      default: true,
    },
    useFloatingActionButtons: {
      type: Boolean,
      default: false,
    },
    moduleAnimationsEnabled: {
      type: Boolean,
      default: true,
    },
    showMysteryControls: {
      type: Boolean,
      default: true,
    },
    themeOverride: {
      type: String,
      default: null,
    },
    useWideNotes: {
      type: Boolean,
      default: false,
    },
    progressTrackingData: {
      type: Object,
      default: () => (
        {
          enabled: false,
          meta: {},
          source: null,
        }
      ),
    },
    enableHighlight: {
      type: Boolean,
      default: true,
    },
    textSelectMetaData: {
      type: Object,
      default: () => {},
    },
    eventsEnabled: {
      type: Boolean,
      default: true,
    },
    submitAssignmentButton: {
      type: Object,
      default: null,
    },
    zoomPercentage: {
      type: Number,
      default: 100,
    },
  },
  provide() {
    return {
      getEditorRef: () => this.$refs.editor,
      getCanvasRefs: () => ({
        modules: this.$refs.editorCanvasCarousel?.getCanvasRef?.(),
        modalModules: this.$refs.modalCanvas,
        notes: this.$refs.notesCanvas,
      }),
    };
  },
  created() {
    const { progressTrackingData } = this;
    this[types.SET_PROGRESS_TRACKING_DATA](progressTrackingData);

    this.getCurrentDraft();

    this.throttleResizeEditor = throttle(() => {
      const { editorStage } = this.$refs;
      if (editorStage) {
        this.editorStageWidth = editorStage.offsetWidth;
      }
      if (this.editorIsFullscreen) {
        this.calculateEditorTransformFullscreen();
      } else {
        this.calculateEditorTransform();
      }
      bus.emit('editor:resized');
    }, 5);

    this.resetActivityTimeout = throttle(() => {
      if (!this.isConnected
        || this.$route?.name !== 'edit'
        || this.mode !== 'EDIT'
        || this.immersiveReaderOpen) return;
      this.clearActivityTimeout();
      /*
        After 15 minutes of no activity, close editing session and
        show still editing confirm modal.
      */
      this.activityTimeout = setTimeout(() => {
        if (this.$route?.name !== 'edit') return;
        this[types.OPEN_MODAL]({
          type: 'ConfirmStillEditingModal',
          onContinueEditing: this.getCurrentDraft.bind(this),
        });
        this.endEditingSession();
      }, 1000 * 60 * 15); // 15 minutes
    }, 5000);

    this.onEditorScrollThrottled = throttle(() => {
      this.$nextTick(() => {
        bus.emit('editor:scroll');
      });
      if (this.editorIsFullscreen) return;

      if (this.suppressScrollEvent) {
        this.suppressScrollEvent = false;
        return;
      }
      this.resetTopScrollPercent();
      this.resetLeftScrollPercent();
      this.updateZoomSlideButtons();
    }, 10);

    this.onWindowScrollThrottled = throttle(() => {
      this.updateZoomSlideButtons();
    }, 10);

    this.onLayoutHeightUpdated = throttle((height) => {
      const parsedHeight = parseFloat(height, 10);
      const originalHeight = this.layoutHeight;
      this.layoutHeight = Math.max(parsedHeight, EDITOR_CANVAS_MIN_LAYOUT_HEIGHT);
      /*
        Only transform content and update scroll percent if the height actually changed.
        Otherwise, the scroll position will not be maintained when zooming in and out.
      */
      if (originalHeight !== this.layoutHeight) {
        this.transformZoomContent();
        this.resetTopScrollPercent();
      }
    }, 5);

    this.setTextSelectPositionThrottled = throttle(this.setTextSelectPosition, 100);

    // In case we have any draft data leftover from a previous load, clear it out
    // to prevent a flash of old data
    if (!this.embedded && this.loadDraft) this[types.CLEAR_DRAFT]();
    if (this.loadDraft && this.mode === 'VIEW') {
      this.$nextTick(() => {
        this.emitViewEvent();
      });
    }

    // Attempt to load the highlight data.
    this.refreshHighlightData();

    if (typeof this.eventsEnabled === 'boolean') {
      this[types.SET_EVENTS_ENABLED](this.eventsEnabled);
    }
  },
  mounted() {
    this.updateRefs();

    bus.on('requestFullscreen', this.onRequestFullscreen);
    bus.on('exitFullscreen', this.onExitFullscreen);
    fscreen.addEventListener('fullscreenchange', this.onFullscreenChange);
    bus.on('requestImmersiveReader', this.onRequestImmersiveReader);
    bus.on('invalidPlacementAttempted', this.displayInvalidPlacementMessage);
    bus.on('document:keydown', this.onKeydown);
    bus.on('module:rotation-start', this.onModuleRotationStart);
    bus.on('module:rotation-end', this.onModuleRotationEnd);

    bus.on('text-select-tools:close-tools', this.onCloseTextSelectTools);
    bus.on('highlight:color-change', this.onHighlightColorChange);
    bus.on('note:changed', this.refreshHighlightData);

    window.addEventListener('scroll', this.onWindowScrollThrottled);

    // Listen for activity on board
    this.toggleActivityListeners(this.mode === 'EDIT');

    if (this.enableTextSelectTools) {
      this.toggleTextSelectHandlers(true);
    }

    // Try to set the text select tools key if available.
    this.$nextTick(() => {
      this.setTextSelectToolsKey();
    });
  },
  beforeUnmount() {
    // Detach canvas and addmodule listeners.
    bus.off('activeModule');
    bus.off('updateDraggableXY');
    bus.off('initPlaceholderModule');
    bus.off('beginPreview');

    bus.off('requestFullscreen', this.onRequestFullscreen);
    bus.off('exitFullscreen', this.onExitFullscreen);
    fscreen.removeEventListener('fullscreenchange', this.onFullscreenChange);
    bus.off('requestImmersiveReader', this.onRequestImmersiveReader);
    bus.off('invalidPlacementAttempted', this.displayInvalidPlacementMessage);
    if (this.$refs.editorStage) {
      this.$refs.editorStage.removeEventListener('scroll', this.onEditorScrollThrottled);
    }
    bus.off('document:keydown', this.onKeydown);
    bus.off('module:rotation-start', this.onModuleRotationStart);
    bus.off('module:rotation-end', this.onModuleRotationEnd);

    bus.off('text-select-tools:close-tools', this.onCloseTextSelectTools);
    bus.off('highlight:color-change', this.onHighlightColorChange);
    bus.off('note:changed', this.refreshHighlightData);

    window.removeEventListener('scroll', this.onWindowScrollThrottled);

    this.toggleTextSelectHandlers(false);
    this.toggleHighlightHandlers(this.highlights, false);

    // Detach activity timeout tracking
    this.toggleActivityListeners(false);

    this.endEditingSession();
    this.destroyCanvasResizeObserver();

    // remove tracking listeners
    if (this.eventsEnabled && this.progressTracking?.enabled) {
      this[types.END_SESSION_TRACKING]();
    }
  },
  computed: {
    currentPageTextSelectKey() {
      /*
        Make sure the draft and all its assets are fully loaded
        and the current page is displayed before rendering highlights.
        Highlights will only be rendered once a valid key is returned here.
      */
      if (
        this.enableTextSelectTools
        && this.highlightData
        && this.currentPageAssetsLoaded
        && this.displayedPageId === this.currentPageId
      ) {
        return this.currentPageId;
      }
      return null;
    },
    apiUrl() {
      return getDomains().deDomain.replace('/v1', '');
    },
    contentId() {
      return `canvas-${this.currentPageId}`;
    },
    highlightMetaData() {
      // grab the provided meta
      const meta = { ...this.textSelectMetaData };

      // apply reference ids to the meta for api compatibility
      if (meta.references) {
        meta.references.forEach((ref) => {
          meta[`${ref.reference_type}_id`] = ref.reference_id;
        });
      }

      // apply an asset guid
      meta.target_asset_id = this.draftAssetId();

      // apply a page id
      meta.page_id = this.currentPageId;

      // add legacy fields
      meta.token = this.apiToken;

      // no asset? use the target
      if (!meta.asset_id) {
        meta.asset_id = meta.target_asset_id;
      }

      // also remove refs if supplied
      if (meta.references) {
        delete meta.references;
      }

      return meta;
    },
    highlightInitData() {
      return {
        highlightsData: this.currentPageHighlightData,
        csrf_tokens: {
          notebook: {
            highlight: this.apiToken,
          },
        },
        feature_flags: {
          'note-taking-techbook-2024': true,
        },
      };
    },
    currentPageIsFullSlideTEI() {
      return !!(
        this.teis
        && this.currentPage.options?.asset_id
        && this.teis[this.currentPage.options?.asset_id]
      );
    },
    textSelectContextContainer() {
      return this.currentPageIsFullSlideTEI ? `#full-slide-${this.currentPage.options?.asset_id}` : `#canvas-${this.currentPageId}`;
    },
    nodeSelectors() {
      let primarySelector;

      if (!this.currentPageIsFullSlideTEI) {
        primarySelector = `${this.textSelectContextContainer} .vue-grid-item`;
      } else {
        primarySelector = `${this.textSelectContextContainer} .tei-instructions-card__content`;
      }

      return {
        primary: [primarySelector],
        target: [':scope > *'],
      };
    },
    enableTextSelectTools() {
      return this.mode === 'VIEW' && this.enableHighlight && this.isDraftLoaded;
    },
    isActive() {
      return this.currentPage.id === this.currentPageId;
    },
    ...mapState(useEditorStore, [
      'draft',
      'editorDrawers',
      'closestFullscreenVisiblePage',
      'currentModalCanvasId',
      'currentPageId',
      'currentPage',
      'editorIsFullscreen',
      'editorViewModePreview',
      'editorSnapGrid',
      'pagesSorted',
      'draftHasTEIErrors',
      'draftType',
      'draftAccessLevel',
      'draftAssetId',
      'userIsStudent',
      'currentPageAssetsLoaded',
      'currentPageIndex',
      'draftPointerAssetId',
      'draftCanBeRestored',
      'draftName',
      'isModuleClipboardEmpty',
      'gridIsDisabled',
      'isMysteryTheme',
      'productSettings',
      'isConnected',
      'isPreviewMode',
      'canUndo',
      'canRedo',
      'moduleClipboard',
      'activeCanvasName',
      'studioAppIsRunning',
      'studentView',
      'displayedPageId',
      'isEditingRestricted',
      'draftIsDeCreated',
      'draftIsLesson',
    ]),
    ...mapState(useTextSelectStore, [
      'highlights',
      'highlightData',
      'currentPageHighlightData',
    ]),
    ...mapState(useUserStore, [
      'user',
      'userIsStudent',
      'isRtl',
      'productSettings',
    ]),
    ...mapState(useModalStore, [
      'modals',
    ]),
    ...mapState(useAppStore, [
      'loading',
      'apiToken',
    ]),
    ...mapState(useAssessmentStore, [
      'unsubmittedAttempt',
      'teis',
    ]),
    ...mapState(useTrackingStore, [
      'progressTracking',
      'activitySummary',
    ]),
    scrollTrackingEnabled() {
      return this.mode === 'VIEW' && (this.currentPage.options?.type === 'page' || this.draftType === 'board');
    },
    draftId() {
      return this.draft?.id;
    },
    allowAttempt() {
      const query = get(this, '$route.query', {});
      return !!query.homework_id;
    },
    editorAriaLabel() {
      if (this.mode === 'EDIT') {
        if (this.draftType === 'slides') return this.$t('Slideshow Editor');
        if (this.draftType === 'lesson') return this.$t('Lesson Editor');
        return this.$t('Board Editor');
      }

      if (this.draftType === 'slides') return this.$t('Slideshow Viewer');
      if (this.draftType === 'lesson') return this.$t('Lesson Viewer');
      return this.$t('Board Viewer');
    },
    embeddedEditorHeight() {
      if (!this.embedded) return null;
      const contentHeight = this.layoutHeight + this.canvasBorderWidth * 2;
      const height = contentHeight * this.scale + this.verticalCanvasPadding * 2;
      return `${height}px`;
    },
    currentPageId() {
      return this.currentPage && this.currentPage.id;
    },
    isAssetSlide() {
      if (!this.draft.pages) return null;

      const pageId = this.pageId || this.currentPageId;
      const page = this.draft.pages.find((p) => (p.id === pageId));

      return !!get(page, 'options.asset_id');
    },
    isDraftLoaded() {
      if (this.loading || this.draft?.unloaded || !this.currentPage) return false;

      // This property should change each time a new draft is fully loaded,
      // so it needs to trigger (only) once for each draft. If it returned
      // the first page id it would trigger too often; if it returned a
      // boolean it would trigger too seldom
      if (this.draft.id && this.draft.pages.length && this.draft.pages[0].id) {
        return this.draft.id;
      }

      return false;
    },
    isSlidesLayout() {
      return this.draftType !== 'board';
    },
    isThumbnailGenerator() {
      return !!getLocalData();
    },
    chatOpen() {
      return this.editorDrawers.chat && !this.currentModalCanvasId;
    },
    customizationOpen() {
      return this.editorDrawers.customization && !this.currentModalCanvasId;
    },
    addModuleOpen() {
      return this.editorDrawers.addModule && !this.currentModalCanvasId;
    },
    editorThemeClasses() {
      const themePattern = get(this, 'currentPage.options.theme.pattern');
      const themeName = get(this, 'currentPage.options.theme.name', 'default');
      const themeVariation = get(this, 'currentPage.options.theme.variation', 1);

      return [
        themePattern && `editor-theme--${themePattern}`, // used for background images
        themeName && `editor-theme--${themeName}`, // used for button and text themes
        themeName && `editor-theme--${themeName}-${themeVariation}`, // used for background colors
      ];
    },
    notesOpen() {
      if (this.currentModalCanvasId) return false;

      return this.editorIsFullscreen
        ? this.editorDrawers.notesFullscreen
        : this.editorDrawers.notes;
    },
    pages() {
      return this.pagesSorted;
    },
    page() {
      return this.currentPage;
    },
    canvasMode() {
      if (this.mode === 'VIEW') return this.mode;
      return this.editorViewModePreview ? 'VIEW' : 'EDIT';
    },
    prevPageDisabled() {
      return this.currentPageIndex === 0;
    },
    nextPageDisabled() {
      return this.currentPageIndex === this.pages.length - 1;
    },
    showAddModule() {
      // The add menu is shown in edit mode, and hidden for the thumbnail generator
      // or when a modal is open
      return this.canvasMode === 'EDIT'
        && !this.isThumbnailGenerator;
    },
    showChatDrawer() {
      // Access to chat is controlled by a product setting
      if (!get(this.productSettings, `${PRODUCT_SETTING_STUDIO_CHAT_ENABLED}.value`)) {
        return false;
      }

      // The chat is shown in edit mode, and hidden for the thumbnail generator
      return this.canvasMode === 'EDIT' && !this.isThumbnailGenerator && !this.currentModalCanvasId;
    },
    showCustomizationDrawer() {
      // The customization drawer is hidden for the thumbnail generator
      // or when the another drawer is open
      return (
        this.canvasMode === 'EDIT'
        && !this.editorDrawers.notes
        && !this.isThumbnailGenerator
        && !this.isEditingRestricted
      );
    },
    showNotesCanvas() {
      // The notes panel and toggle is only available for teachers, and only shows
      // in VIEW mode if there are notes for the current page. It's also hidden
      // for the thumbnail generator and when the customization drawer is open.
      return (!this.userIsStudent || !this.user)
        && (this.page?.notes?.length || (this.mode === 'EDIT' && !this.editorViewModePreview))
        && !this.isThumbnailGenerator
        && !this.editorDrawers.customization
        && this.notesVisible
        && !this.draftRestoreEnabled
        && !this.pageDrawer;
    },
    showImmersiveReader() {
      return !this.editorIsFullscreen
        && (!(this.notesOpen || this.addModuleOpen || this.customizationOpen)
        && this.currentPageAssetsLoaded)
        && this.useImmersiveReader
        && !this.isThumbnailGenerator
        && !this.isPreviewMode
        && !this.draftRestoreEnabled
        && !this.currentModalCanvasId;
    },
    showImmersiveReaderFullscreen() {
      return this.useImmersiveReader
        && this.currentPageAssetsLoaded
        && !this.draftRestoreEnabled;
    },
    fullscreenEnabled() {
      return fscreen.fullscreenEnabled;
    },
    noPagesVisibleFullscreen() {
      return this.editorIsFullscreen
        && every(get(this, 'draft.pages'), (page) => get(page, 'options.fullscreen_skip'));
    },
    showPreviousSlideButton() {
      return (this.isSlidesLayout || this.studentView)
        && (this.slideButtonsVisibleDisabled || !this.prevPageDisabled)
        && !this.editorIsFullscreen
        && !this.isThumbnailGenerator
        && this.slideButtonsVisible
        && !this.isMysteryFullSlideOverlay()
        && this.navigationType === 'default';
    },
    showNextSlideButton() {
      return (this.isSlidesLayout || this.studentView)
        && (this.slideButtonsVisibleDisabled || !this.nextPageDisabled)
        && !this.editorIsFullscreen
        && !this.isThumbnailGenerator
        && this.slideButtonsVisible
        && !this.isMysteryFullSlideOverlay()
        && this.navigationType === 'default';
    },
    scaleZoomed() {
      return (this.zoomLevel / 100) * this.scale;
    },
    initialScrollPercentTop() {
      return this.isSlidesLayout ? 0.5 : 0;
    },
    initialScrollPercentLeft() {
      return this.isRtl ? -0.5 : 0.5;
    },
    showEditorToolbar() {
      return (!this.embedded || this.mode === 'EDIT')
        && !this.isThumbnailGenerator
        && this.toolbarVisible
        && !this.editorIsFullscreen;
    },
    isUsingCircleSlideButtons() {
      return (this.isSlidesLayout || this.studentView)
        && this.slideButtonsVisible
        && this.slideButtonType === 'circle' && !this.isMysteryFullSlideOverlay()
        && !this.isThumbnailGenerator;
    },
    slideButtonSize() {
      return this.editorStageWidth < this.mobileLandscape ? 's' : 'l';
    },
    horizontalCanvasPadding() {
      const defaultPadding = () => {
        const circleButtonPadding = this.slideButtonSize === 'l'
          ? EDITOR_CANVAS_PADDING_HORIZONTAL_CIRCLE_BUTTON
          : EDITOR_CANVAS_PADDING_HORIZONTAL_CIRCLE_BUTTON_SMALL;
        return this.isUsingCircleSlideButtons
          ? circleButtonPadding
          : EDITOR_CANVAS_PADDING;
      };
      if (isNumber(this.canvasPadding?.horizontal)) {
        return this.canvasPadding?.horizontal;
      }
      if (!isNumber(this.canvasPadding)) {
        return defaultPadding();
      }
      return this.canvasPadding;
    },
    verticalCanvasPadding() {
      const defaultPadding = () => {
        if (isMobileOrTablet() || this.editorStageWidth < this.mobileLandscape) {
          return this.showZoomContentHeader
            ? EDITOR_CANVAS_PADDING_MOBILE_VERTICAL_WITH_HEADER
            : EDITOR_CANVAS_PADDING_MOBILE_VERTICAL;
        }
        return this.showZoomContentHeader
          ? EDITOR_CANVAS_PADDING_VERTICAL_WITH_HEADER
          : EDITOR_CANVAS_PADDING;
      };
      if (isNumber(this.canvasPadding?.vertical)) {
        return this.canvasPadding?.vertical;
      }
      if (!isNumber(this.canvasPadding)) {
        return defaultPadding();
      }
      return this.canvasPadding;
    },
    showUndoRedoButtons() {
      return this.mode === 'EDIT' && !this.editorViewModePreview;
    },
    draftRestoreEnabled() {
      return this.draftCanBeRestored
        && !this.embedded
        && !(this.allowAttempt && this.userIsStudent);
    },
    allowSnapGrid() {
      return this.gridIsDisabled && this.mode === 'EDIT' && this.draftIsDeCreated;
    },
    snapGrid() {
      return this.gridIsDisabled && this.canvasMode === 'EDIT' && this.editorSnapGrid
        ? this.editorSnapGrid : undefined;
    },
    showZoomContentHeader() {
      return !!this.canvasBadges && !this.editorIsFullscreen;
    },
    zoomContentHeaderHeight() {
      return this.showZoomContentHeader
        ? EDITOR_ZOOM_CONTENT_HEADER_HEIGHT + this.nebulaSpace1x
        : 0;
    },
  },
  watch: {
    draftId() {
      this.$nextTick(() => {
        // enable maze if needed once the draft has been loaded
        if (
          window
          && get(this.productSettings, `${PRODUCT_SETTING_MAZE_TRACKING_ENABLED}.value`)
          && this.draft.options?.maze_enabled
          && !this.userIsStudent
          && !this.isThumbnailGenerator
        ) {
          // add the maze script
          const mazeScript = window.document.createElement('script');
          mazeScript.setAttribute(
            'src',
            '/js/maze.js',
          );
          document.head.appendChild(mazeScript);
        }
      });
    },
    height() {
      this.$nextTick(() => {
        this.throttleResizeEditor();
      });
    },
    zoomPercentage() {
      this.$nextTick(() => {
        this.onZoomChanged(this.zoomPercentage);
      });
    },
    currentPageTextSelectKey() {
      /*
        Once the draft and its assets for the current page are fully loaded,
        wait for everything to be rendered in the DOM before setting
        the text select tools key which will cause the highlights for the
        current page to be rendered.
        If a key is not available yet, then text select tools will be removed until a valid
        key is provided when the current page is ready.
      */
      this.$nextTick(() => {
        this.setTextSelectToolsKey();
      });
    },
    immersiveReaderOpen() {
      if (this.immersiveReaderOpen) {
        this.clearActivityTimeout();
      } else {
        this.resetActivityTimeout();
      }
    },
    mode() {
      this.toggleActivityListeners(this.mode === 'EDIT');
    },
    currentPageIndex() {
      this.$nextTick((val) => {
        // Begin each new page at the top
        if (val !== -1 && this.$refs.editorStage && this.$refs.editorStage.scrollTo) {
          this.$refs.editorStage.scrollTo(0, 0);
        }
      });
    },
    currentPageId(newPageId) {
      // Send a clicklog event when we change pages
      if (this.mode === 'VIEW') {
        this.$nextTick(() => {
          this.emitViewEvent();
        });
      }

      this.setQueryStringPageId();

      this.$nextTick(() => {
        this.suppressScrollEvent = true;
        this.initScrollPercents();
        this.updateZoomContentDisplay();
      });

      const gridDisabled = this.currentPage?.options?.grid_disabled;

      // everything has the grid disabled now, so if this is
      // a 'legacy' board/slideshow, disable the grid
      if (this.mode === 'EDIT' && !gridDisabled) {
        this[types.DISABLE_GRID_FOR_PAGE]({ pageId: newPageId });
      }
    },
    draftName() {
      this.updateDocumentTitle();
    },
    isDraftLoaded(loaded) {
      if (!loaded) this.enableZoomTransition = false;
      this.updateDocumentTitle();
      this.$nextTick(() => {
        this.updateRefs();
        this.initScrollPercents();
        if (loaded) this.enableZoomTransition = true;
      });

      // Attempt to load the highlight data.
      if (!this.isThumbnailGenerator) {
        this.removeHighlightData();
        this.refreshHighlightData();
      }
    },
    $route() {
      if (this.suppressRouteChangeEvents) {
        this.suppressRouteChangeEvents = false;
        return;
      }
      this.$nextTick(() => {
        this.getCurrentDraft();
      });
    },
    scaleZoomed() {
      bus.emit('editor:zoom-changed', this.scaleZoomed);
    },
    isConnected(connected) {
      if (connected) {
        this.$nextTick(() => {
          this.throttleResizeEditor();
        });
        if (this.mode === 'EDIT') {
          this.resetActivityTimeout();
        }
      }
    },
    zoomLevel() {
      this.updateZoomContentDisplay();
    },
    canvasPadding() {
      this.updateZoomContentDisplay();
    },
    isRtl() {
      this.initScrollPercents();
      this.updateZoomContentDisplay();
    },
    editorIsFullscreen() {
      this.suspendNotesAnimation();
      if (!this.editorIsFullscreen) {
        this.closeImmersiveReader();
      } else if (this.isMysteryTheme) {
        // close the drawer for mystery slides
        this[types.TOGGLE_EDITOR_DRAWER]({ drawer: 'notesFullscreen', open: false });
      }
      this.onCloseTextSelectTools();
    },
    notesOpen() {
      /*
        Ensure the notes grid layout layout resizes itself when opened.
      */
      if (this.notesOpen) {
        this.$nextTick(bus.dispatchWindowResize);
      }
    },
    currentModalCanvasId() {
      if (this.currentModalCanvasId) {
        this.modalZoomLevel = 100;
      }
    },
    enableTextSelectTools() {
      this.toggleTextSelectHandlers(this.enableTextSelectTools);
    },
  },
  methods: {
    ...mapActions(useConfirmationStore, [
      types.CLEAR_CONFIRMATIONS,
      types.CONFIRM_ACTION,
    ]),
    ...mapActions(useErrorStore, [
      types.SET_ERROR,
    ]),
    ...mapActions(useEditorStore, [
      types.VIEW_CLOSEST_FULLSCREEN_VISIBLE_PAGE,
      types.VIEW_PREV_PAGE,
      types.VIEW_NEXT_PAGE,
      types.SET_EDITOR_IS_FULLSCREEN,
      types.SET_EDITOR_VIEW_MODE_PREVIEW,
      types.SET_EDITOR_SNAP_GRID,
      types.CLEAR_DRAFT,
      types.GET_DRAFT,
      types.COPY_CREATION,
      types.GET_CREATION,
      types.TOGGLE_EDITOR_DRAWER,
      types.GET_TEMPLATES,
      types.DISABLE_GRID_FOR_PAGE,
      types.OPEN_EDITING_SESSION,
      types.CLOSE_EDITING_SESSION,
      types.UNDO_DRAFT_EDIT,
      types.REDO_DRAFT_EDIT,
      types.SET_ACTIVE_CANVAS_NAME,
    ]),
    ...mapActions(useTextSelectStore, [
      types.SET_HIGHLIGHTS,
      types.ADD_HIGHLIGHT,
      types.REMOVE_HIGHLIGHT,
      types.UPDATE_HIGHLIGHT,
      types.GET_HIGHLIGHT_DATA,
      types.CLEAR_HIGHLIGHT_DATA,
    ]),
    onTextSelectCoordsReset({ x, y }) {
      const viewportX = (x || 0) - window.scrollX;
      const viewportY = (y || 0) - window.scrollY;
      const elements = document.elementsFromPoint(viewportX, viewportY);
      const trigger = elements.find((el) => el.classList.contains('de-highlight'))
        || first(elements);
      if (!trigger) {
        this.textSelectMenuTrigger = null;
        return;
      }
      const triggerRect = trigger.getBoundingClientRect();
      const xPercentage = (viewportX - triggerRect.left) / triggerRect.width;
      const yPercentage = (viewportY - triggerRect.top) / triggerRect.height;
      /*
        Remember the element that was clicked and the percent distance from
        its edges that the pointer coordinates are. We will use this as the
        canvas resizes, scrolls and zooms to reposition the menu accordingly.
      */
      this.textSelectMenuTrigger = {
        trigger,
        xPercentage,
        yPercentage,
      };
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.setTextSelectPositionThrottled();
        });
      });
    },
    toggleTextSelectHandlers(active) {
      const toggleHandlers = (on) => {
        const busFunc = on ? 'on' : 'off';
        bus[busFunc]('editor:zoom-changed', this.setTextSelectPositionThrottled);
        bus[busFunc]('window:scroll', this.setTextSelectPositionThrottled);
        bus[busFunc]('editor:resized', this.setTextSelectPositionThrottled);
        bus[busFunc]('editor:scroll', this.setTextSelectPositionThrottled);
        bus[busFunc]('text-editor:scroll', this.setTextSelectPositionThrottled);
        bus[busFunc]('module:scroll', this.setTextSelectPositionThrottled);
      };
      toggleHandlers(false);
      if (active) {
        toggleHandlers(active);
      }
    },
    setTextSelectPosition() {
      const { editorStage, textSelect } = this.$refs;
      if (!editorStage || !textSelect) return;
      const textSelectEl = textSelect.$refs.textSelect;
      if (!textSelectEl) return;
      const stageRect = pick(editorStage.getBoundingClientRect(), [
        'top', 'left', 'bottom', 'right',
      ]);
      const stageMargin = this.nebulaSpace1x * 2;
      stageRect.left += stageMargin;
      stageRect.top += stageMargin;
      stageRect.right -= stageMargin;
      stageRect.bottom -= stageMargin;
      const toolsRect = textSelectEl.getBoundingClientRect();
      const { trigger, xPercentage, yPercentage } = this.textSelectMenuTrigger;
      if (!trigger) return;
      const triggerRect = trigger.getBoundingClientRect();
      /*
        Get the new position by finding the pointer coordinates that are
        certain percentages from the clicked element's edges.
      */
      let toolsLeft = triggerRect.left + (triggerRect.width * xPercentage);
      let toolsTop = triggerRect.top + (triggerRect.height * yPercentage);
      const toolsRight = toolsLeft + toolsRect.width;
      const toolsBottom = toolsTop + toolsRect.height;
      /*
        Make any adjustments needed to keep the menu within the bound
        of the editor stage element.
      */
      if (toolsRight > stageRect.right) toolsLeft -= toolsRight - stageRect.right;
      if (toolsBottom > stageRect.bottom) toolsTop -= toolsBottom - stageRect.bottom;
      if (toolsLeft < stageRect.left) toolsLeft += stageRect.left - toolsLeft;
      if (toolsTop < stageRect.top) toolsTop += stageRect.top - toolsTop;
      // Update the pointer coordinates for the text select tools menu.
      textSelect.pointerCoordinates.x = toolsLeft;
      textSelect.pointerCoordinates.y = toolsTop;
    },
    toggleHighlightHandlers(highlights, on) {
      forEach(highlights, (h) => {
        h.eventBus[on ? 'on' : 'off']('select', this.onHighlightSelected);
        h.eventBus[on ? 'on' : 'off']('deselect', this.onHighlightDeselected);
      });
    },
    destroyHighlights() {
      /*
        Ensure current highlights are removed from the DOM
        and event handlers are also removed.
      */
      this.toggleHighlightHandlers(this.highlights, false);
      forEach(this.highlights, (h) => h.destroy());
    },
    onHighlightSelected(args) {
      bus.emit('highlight:selected', args);
    },
    onHighlightDeselected(args) {
      bus.emit('highlight:deselected', args);
    },
    onHighlightCreated(highlight) {
      this.toggleHighlightHandlers([highlight], true);
      this[types.ADD_HIGHLIGHT](highlight);
      this.refreshHighlightData();
    },
    onHighlightUpdated(highlight) {
      this[types.UPDATE_HIGHLIGHT](highlight);
      this.refreshHighlightData();
    },
    onHighlightDeleted(highlight) {
      this.toggleHighlightHandlers([highlight], false);
      this[types.REMOVE_HIGHLIGHT](highlight.id);
      this.refreshHighlightData();
    },
    onHighlightsRetrieved(highlights) {
      // Destroy current highlights.
      this.destroyHighlights();

      // Then set current highlights.
      this[types.SET_HIGHLIGHTS](highlights);

      // Then attach highlight event handlers.
      this.toggleHighlightHandlers(highlights, true);
    },
    onHighlightColorChange(highlight) {
      const { textSelect } = this.$refs;
      if (textSelect) {
        textSelect.saveHighlight(highlight);
      }
      this.refreshHighlightData();
    },
    onCreateHighlightNote(highlight) {
      bus.emit('highlight-note:create', { highlight });
    },
    onShowHighlightNote(note) {
      bus.emit('highlight-note:show', { note });
    },
    onCloseTextSelectTools() {
      const { textSelect } = this.$refs;
      if (textSelect) {
        textSelect.closeTools();
      }
    },
    removeHighlightData() {
      this.toggleHighlightHandlers(this.highlights, false);
      this[types.CLEAR_HIGHLIGHT_DATA]();
    },
    async refreshHighlightData() {
      if (!this.enableTextSelectTools) return;

      // use all ids minus page id to get unique reference params
      const referenceIds = [...new Set(Object.keys(this.highlightMetaData)
        .filter((key) => key.includes('_id') && key !== 'page_id')
        .map((key) => this.highlightMetaData[key]))];

      this[types.GET_HIGHLIGHT_DATA](referenceIds);
    },
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
      types.CLEAR_MODALS,
    ]),
    ...mapActions(useTrackingStore, [
      types.SEND_EVENT,
      types.SEND_CLICKLOG_EVENT,
      types.SET_PROGRESS_TRACKING_DATA,
      types.STORE_PAGE_VIEW_PERCENTAGE,
      types.SET_EVENTS_ENABLED,
      types.START_SESSION_TRACKING,
      types.END_SESSION_TRACKING,
    ]),
    ...mapActions(useMessageStore, [
      types.SET_APP_MESSAGE,
    ]),
    getVisiblePagePct() {
      if (this.currentPage) {
        // slides are always 100
        if (this.draftType === 'slides' || this.currentPage.options?.type === 'slide') {
          return 100;
        }
        // use scroll loc to determine a rough approximation of pct
        const stageEl = this.$refs.editorStage;
        // grab scroll vals and roughly how much of the page is visible in the window
        const scrollVal = stageEl.scrollTop;
        const scrollHeight = (stageEl.scrollHeight - stageEl.offsetHeight);
        const offset = (window.innerHeight - (64 + this.verticalCanvasPadding))
          / (this.zoomLevel / 100);
        // calculate a pct value. since this is based on scroll and then offset by
        // visible height, values can extend past 100
        const scrollPct = Math.round(((scrollVal + offset) / scrollHeight) * 100);
        const percMap = [100, 75, 50, 25, 0];
        for (let x = 0; x < percMap.length; x += 1) {
          // find a map value to match
          if (scrollPct >= percMap[x]) {
            return percMap[x];
          }
        }
      }
      return 0;
    },
    toggleActivityListeners(listen) {
      const docFunc = listen ? 'addEventListener' : 'removeEventListener';
      const busFunc = listen ? 'on' : 'off';
      bus[busFunc]('document:mousedown', this.resetActivityTimeout);
      bus[busFunc]('document:keyup', this.resetActivityTimeout);
      bus[busFunc]('window:scroll', this.resetActivityTimeout);
      bus[busFunc]('window:resize', this.resetActivityTimeout);
      bus[busFunc]('audio:audioprocess', this.resetActivityTimeout);
      bus[busFunc]('video:timeupdate', this.resetActivityTimeout);
      document[docFunc]('mousemove', this.resetActivityTimeout);
      document[docFunc]('wheel', this.resetActivityTimeout);
    },
    updateScrollPct(value) {
      let found = false;
      let sendEvent = false;
      const viewData = this.activitySummary.progress.page;
      if (viewData) {
        // walk existing page pct
        for (let x = 0; x < viewData.length; x += 1) {
          if (viewData[x].id === this.currentPage.id) {
            found = true;
            if (viewData[x].pct < value) {
              // toggle send the event only if the value is higher
              sendEvent = true;
            }
          }
        }
      }
      // if we didn't find the given module in the existing list
      if (!found || sendEvent) {
        // update summary
        this[types.STORE_PAGE_VIEW_PERCENTAGE](this.currentPage.id, value);
      }
    },
    onScroll() {
      if (
        this.scrollTrackingEnabled
        && this.activitySummary
      ) {
        this.updateScrollPct(this.getVisiblePagePct());
      }
    },
    isMysteryFullSlideOverlay() {
      return this.showMysteryControls
        && (
          this.currentPage?.options?.theme?.name === 'mystery-science'
          || this.themeOverride === 'mystery'
        )
        && (
          this.mode === 'VIEW'
          || this.editorViewModePreview
        )
        && !this.editorIsFullscreen
        && this.embedded;
    },
    showNavigation() {
      if (
        (
          (this.isMysteryTheme && this.editorIsFullscreen) // not fullscreen with header
          || (this.navigationType !== 'default' && !this.editorIsFullscreen)
        )
        && !this.isMysteryFullSlideOverlay() // mystery overlay doesn't show arrows
      ) {
        return true;
      }
      return false;
    },
    clearActivityTimeout() {
      if (this.activityTimeout) {
        clearTimeout(this.activityTimeout);
        this.activityTimeout = null;
      }
    },
    async endEditingSession() {
      this.clearActivityTimeout();
      await this[types.CLOSE_EDITING_SESSION]();
    },
    focusOpen(drawer) {
      this.$nextTick(() => {
        const drawerRef = this.$refs[drawer];
        if (drawerRef) drawerRef.focus();
      });
    },
    toggleDrawer(drawer) {
      this.updatedEmbeddedHeight = (drawer === 'notes') && this.embeddedEditorHeight;
      this[types.TOGGLE_EDITOR_DRAWER]({ drawer });
    },
    calculateEditorTransform() {
      if (this.$refs?.editorStage) {
        /*
          Allow unlimited scale on mobile so slides can always expand to
          take up all available space.
        */
        const maximumScale = isMobileOrTablet() ? Infinity : 1.44;
        const { editorStage } = this.$refs;
        const editorCanvasWidth = EDITOR_CANVAS_WIDTH + this.canvasBorderWidth * 2;
        const editorCanvasHeight = EDITOR_CANVAS_HEIGHT + this.canvasBorderWidth * 2;
        /*
          The total height of the unscaled vertical area within the zoom window. This determines
          the available space the zoom content can expand into.
        */
        const totalVerticalCanvasPadding = this.verticalCanvasPadding * 2
          + this.zoomContentHeaderHeight;
        /*
          For boards, leave extra space for the scrollbar. We don't want to use the
          clientWidth here because that can cause an endless flickering of the scrollbar.
          Instead, use offsetWidth minus some extra space for the scrollbar.
        */
        const editorStageWidth = this.isSlidesLayout
          ? editorStage.offsetWidth
          : editorStage.offsetWidth - 20; // For boards, leave space for scrollbar.
        this.scale = Math.min(
          (editorStageWidth - this.horizontalCanvasPadding * 2) / editorCanvasWidth,
          this.isSlidesLayout
            ? (editorStage.offsetHeight - totalVerticalCanvasPadding) / editorCanvasHeight
            : Infinity,
          maximumScale,
        );

        this.updateZoomContentDisplay();
      }
    },
    calculateEditorTransformFullscreen() {
      if (this.$refs?.editorStage?.getBoundingClientRect) {
        const { width, height } = this.$refs.editorStage.getBoundingClientRect();
        this.scale = Math.min(
          width / EDITOR_CANVAS_WIDTH,
          this.isSlidesLayout
            ? height / EDITOR_CANVAS_HEIGHT
            : Infinity,
        );
        this.updateZoomContentDisplay();
      }
    },
    setQueryStringPageId() {
      if (this.$router
        && this.$route
        && this.studioAppIsRunning
        && this.currentPageId
        && this.$route.query.page_id !== this.currentPageId
      ) {
        this.suppressRouteChangeEvents = true;
        this.$router.replace({
          query: { ...this.$route.query, page_id: this.currentPageId },
        });
      }
    },
    async goToPrevPage() {
      // Confirm before navigating if it'll overwrite changes
      if (this.mode === 'EDIT' && this.draftHasTEIErrors) {
        this[types.OPEN_MODAL]({
          type: 'TEIErrorModal',
          successCallback: () => {
            this[types.VIEW_PREV_PAGE]();
          },
        });
        return;
      }

      // Confirm before navigating if we have an unsubmitted attempt
      if (this.unsubmittedAttempt) {
        this[types.OPEN_MODAL]({
          type: 'UnsavedAnswersModal',
          successCallback: () => {
            this[types.VIEW_PREV_PAGE]();
          },
        });
        return;
      }

      // Navigate
      await this[types.VIEW_PREV_PAGE]();

      // Send pageview event
      if (this.mode === 'VIEW') {
        this[types.SEND_EVENT]({
          referenceId: this.draft.id,
          eventType: 'nav.prev',
          referenceType: 'creation',
        });
      }
    },
    async goToNextPage() {
      // Confirm before navigating if it'll overwrite changes
      if (this.mode === 'EDIT' && this.draftHasTEIErrors) {
        this[types.OPEN_MODAL]({
          type: 'TEIErrorModal',
          successCallback: () => {
            this[types.VIEW_NEXT_PAGE]();
          },
        });
        return;
      }

      // Confirm before navigating if we have an unsubmitted attempt
      if (this.unsubmittedAttempt) {
        this[types.OPEN_MODAL]({
          type: 'UnsavedAnswersModal',
          successCallback: () => {
            this[types.VIEW_NEXT_PAGE]();
          },
        });
        return;
      }

      // Navigate
      await this[types.VIEW_NEXT_PAGE]();

      // Send pageview event
      if (this.mode === 'VIEW') {
        this[types.SEND_EVENT]({
          referenceId: this.draft.id,
          eventType: 'nav.next',
          referenceType: 'creation',
        });
      }
    },
    emitViewEvent() {
      if (!this.page) return;

      if (this.currentPageIndex > -1) {
        // Send an event with the human-readable index, which is used in suite's
        // UI and for activity tracking
        bus.emit('nav:pageChanged', { page: this.currentPageIndex + 1 });
      }

      // wait 500ms for assets to load, transitions to complete
      // so that tracking progress calculations will be more accurate
      // eventually we should put this behind something reactive
      const pageId = this.page.id;

      setTimeout(() => {
        // calculate visible pct after the dom has been given
        // a chance to complete rendering
        const visiblePage = this.getVisiblePagePct();

        this[types.SEND_EVENT]({
          referenceId: pageId,
          eventType: 'page.view',
          referenceType: 'page',
          value: visiblePage,
        });
      }, 500);

      // Prepare clicklog asset ids (all embedded DE assets in this page)
      const assetIds = [...this.page.modules, ...(this.page.notes || [])]
        .filter((mod) => mod.type === 'asset' && mod.options.asset_id)
        .map((mod) => mod.options.asset_id);

      this[types.SEND_CLICKLOG_EVENT]({
        ids: assetIds,
      });
    },
    updateDocumentTitle() {
      if (!this.draft.options || this.embedded) return;
      document.title = `${this.draftName} | ${this.$t('Studio | Discovery Education')}`;
    },
    updateRefs() {
      this.editorRef = this.$refs.editor;
      this.editorScrollContainer = this.$refs.editorStage;

      this.createCanvasResizeObserver();

      if (this.$refs.editorStage) {
        this.$refs.editorStage.removeEventListener('scroll', this.onEditorScrollThrottled);
        this.$refs.editorStage.addEventListener('scroll', this.onEditorScrollThrottled);
      }
    },
    async getCurrentDraft() {
      // Wait for the previous session to close before continuing.
      // editing only exists in view
      if (this.loadDraft) {
        await this.endEditingSession();
      }

      // Clear all highlight data
      this.removeHighlightData();

      // Remove confirmations
      this[types.CLEAR_CONFIRMATIONS]();

      // Turn off editor view mode preview
      this[types.SET_EDITOR_VIEW_MODE_PREVIEW](false);

      // Ensure snap grid is off
      this[types.SET_EDITOR_SNAP_GRID](null);

      // reset zoom
      this.zoomLevel = this.zoomPercentage;

      // reset zoom window to default scroll percents
      this.initScrollPercents();

      // grab the source id
      let sourceId;
      if (this.$route?.query) {
        sourceId = this.$route.query.source_id;
      }

      if (!this.loadDraft && !sourceId) {
        /*
          Assume draft is already loaded if this property is set
          to true. Resize and exit.
        */
        this.$nextTick(() => {
          this.throttleResizeEditor();
        });
      } else if (this.isThumbnailGenerator) {
        // The thumbnail generator renders in EDIT mode, but doesn't need
        // to load drafts collaboratively. It can use the same action as before
        const { id: draftId, page_id: pageId } = this.$route.query;

        await this[types.GET_DRAFT]({
          draftId,
          isThumbnailGenerator: true,
          pageId,
          type: this.$route.query.type,
        });
        this.$nextTick(() => {
          this.throttleResizeEditor();
        });
      } else if (this.mode === 'EDIT') {
        const {
          id: draftId,
          page_id: pageId,
          type,
          title,
        } = this.$route.query;

        try {
          if (sourceId) {
            // copy the draft and then load the copy
            const newDraft = await this[types.COPY_CREATION]({
              type: 'creation',
              id: sourceId,
              commitOnSuccess: true,
              title,
              editOnSuccess: true,
            });

            // grab the new pointer
            const reference = newDraft.references.find(ref => ref.reference_subtype === 'head');
            if (reference) {
              // reset the url
              this.$router.replace({
                name: 'edit',
                query: {
                  id: reference.reference_id,
                  page_id: newDraft.pages[0].id,
                },
              });
            } else {
              const errMessage = this.$t('There was an error when attempting to create a copy.');
              this[types.SET_ERROR]({
                active: true,
                error: errMessage,
              });
              throw new Error(errMessage);
            }
          } else {
            await this[types.OPEN_EDITING_SESSION]({
              draftId,
              type,
              pageId,
            });
          }

          this.updateDrawers();
          this[types.GET_TEMPLATES]();
        } catch (error) {
          if (error instanceof DraftIsDeletedError) {
            /*
              If the draft is deleted, redirect and open the draft
              in view mode instead.
            */
            this.$router.replace({
              name: 'view',
              query: {
                id: error.draftPointerAssetId || error.draftId,
                dataType: error.draftIsPublished ? 'creation' : 'draft',
              },
            });
          } else {
            this[types.SET_ERROR]({
              active: true,
              error: get(error, 'response.data', error),
            });
            throw error;
          }
        }
      } else {
        // Get the current published creation (and attempt if necessary).
        // Students can load an attempt if they have a homework id; teachers
        // can load an attempt if they have a student's homework id or are using
        // the query param `attempt=true` to make an unassigned attempt
        const query = get(this, '$route.query', {});
        const dataType = query.dataType || 'creation';

        if (dataType === 'creation') {
          const payload = {
            allowAttempt: this.allowAttempt,
            id: query.id,
            type: query.type,
            pageId: this.pageId || query.page_id,
            homeworkId: query.homework_id,
            userId: query.user_id,
            useRevision: this.allowAttempt || query.use_revision,
          };
          await this[types.GET_CREATION](payload);
        } else if (query.source_id) {
          // copy the draft and then load the copy
          await this[types.COPY_CREATION]({
            type: query.type,
            id: query.source_id,
            commitOnSuccess: true,
          });
        } else {
          await this[types.GET_DRAFT]({
            draftId: query.id,
            type: query.type,
            sourceId: query.source_id,
            commitOnSuccess: true,
          });
        }

        /*
          If the draft or creation is deleted and is not DE created, and the user is not
          making an attempt on a Studio Quiz, show a message that allows them to restore
          the draft or creation.
        */
        if (this.draftRestoreEnabled) {
          /*
            If the draft is deleted and the dataType is draft and it has been published or
            if the creation is deleted and it's not the latest revision, we want to redirect
            to the latest published creation so we can restore using that creation.id instead.
            For drafts that have been published, we always want to restore using the 'id' of the
            latest published creation, otherwise we restore with the draft.id.
          */
          const isPreviousRevision = dataType === 'creation' && query.id !== this.draftPointerAssetId();
          const isPublishedDraft = dataType === 'draft' && this.draft.metadata.published_on;
          if (this.draftPointerAssetId() && (isPublishedDraft || isPreviousRevision)) {
            this.$router.replace({
              name: 'view',
              query: {
                id: this.draftPointerAssetId(),
              },
            });
            return;
          }
          const appMessage = {
            type: 'DeletedCreationMessage',
            creation: this.draft,
            dataType,
          };
          if (['admin', 'owner', 'edit'].includes(this.draftAccessLevel)) {
            appMessage.onRestore = () => {
              this[types.SET_APP_MESSAGE](null);
              // After we restore a draft, redirect to edit mode.
              if (this.draftPointerAssetId()) {
                this.$router.push({
                  name: 'edit',
                  query: {
                    id: this.draftPointerAssetId(),
                  },
                });
              }
            };
          }
          this.$nextTick(() => {
            this[types.SET_APP_MESSAGE](appMessage);
          });
        }

        // Ensure canvas is resized after draft is fully loaded
        this.$nextTick(() => {
          this.throttleResizeEditor();
        });

        // after everything, if we're tracking progress, start the activity checker
        if (
          (this.progressTracking?.enabled || this.eventsEnabled)
          && this.mode === 'VIEW'
          && this.draft?.id
        ) {
          this[types.START_SESSION_TRACKING]();
        }
      }
    },
    suspendNotesAnimation() {
      /*
        Suppress animation for the notes drawer when toggling between fullscreen mode
        since we don't want to see it slide in or out in this case.
      */
      this.notesDrawerAnimated = false;
      if (this.notesDrawerAnimatedTimeout) clearTimeout(this.notesDrawerAnimatedTimeout);
      this.notesDrawerAnimatedTimeout = setTimeout(() => {
        this.notesDrawerAnimated = true;
      }, 250);
    },
    async onRequestFullscreen() {
      if (this.fullscreenEnabled && !this.loading && !fscreen.fullscreenElement) {
        const closestVisiblePage = this.closestFullscreenVisiblePage();
        if (closestVisiblePage?.id !== this.currentPage?.id) {
          if (!(await this[types.CONFIRM_ACTION](`requestFullscreen:${types.VIEW_PAGE_BY_ID}`))) {
            return;
          }
        }
        fscreen.requestFullscreen(this.$refs.editor);
      }
    },
    onExitFullscreen() {
      if (this.fullscreenEnabled && fscreen.fullscreenElement) {
        fscreen.exitFullscreen();
      }
    },
    createCanvasResizeObserver() {
      if (!window.ResizeObserver) return;
      this.destroyCanvasResizeObserver();
      const editorStageEl = this.$refs?.editorStage;
      if (editorStageEl) {
        this.canvasResizeObserver = new ResizeObserver(this.throttleResizeEditor);
        this.canvasResizeObserver.observe(editorStageEl);
      }
    },
    destroyCanvasResizeObserver() {
      if (this.canvasResizeObserver) {
        const editorStageEl = this.$refs?.editorStage;
        if (editorStageEl) {
          this.canvasResizeObserver.unobserve(editorStageEl);
        }
        this.canvasResizeObserver = null;
      }
    },
    async onFullscreenChange() {
      if (fscreen.fullscreenElement && fscreen.fullscreenElement === this.$refs.editor) {
        this[types.SET_EDITOR_IS_FULLSCREEN]({ isFullscreen: true, mode: this.mode });
        /*
          If the current page is hidden in fullscreen mode, then go to the
          next closest page that is visible.
        */
        await this[types.VIEW_CLOSEST_FULLSCREEN_VISIBLE_PAGE]();
        // Ensure that scroll position is reset
        this.$refs.editorStage.scrollTop = 0;
        this.$refs.editorStage.scrollLeft = 0;

        // Close the notes drawer when entering fullscreen mode.
        this[types.TOGGLE_EDITOR_DRAWER]({ drawer: 'notesFullscreen', open: false });
      }
    },
    updateDrawers() {
      // We may get directed to this route and want to begin with the Notes drawer open
      // (e.g. when creating from an Activity Template). This is passed by hash
      if (get(this.$route, 'hash') === '#notes-open') {
        this[types.TOGGLE_EDITOR_DRAWER]({ drawer: 'notes' });
      }
    },
    onZoomChanged(value) {
      if (this.currentModalCanvasId) {
        this.modalZoomLevel = value;
      } else {
        this.zoomLevel = value;
      }
    },
    initScrollPercents() {
      if (this.editorIsFullscreen) return;
      this.scrollPercentTop = this.initialScrollPercentTop;
      this.scrollPercentLeft = this.initialScrollPercentLeft;
    },
    resetTopScrollPercent() {
      if (this.editorIsFullscreen) return;
      const editorStage = get(this, '$refs.editorStage');
      if (!editorStage) return;
      this.scrollPercentTop = editorStage.scrollHeight > editorStage.clientHeight
        ? editorStage.scrollTop / (editorStage.scrollHeight - editorStage.clientHeight)
        : this.initialScrollPercentTop;
    },
    resetLeftScrollPercent() {
      if (this.editorIsFullscreen) return;
      const editorStage = get(this, '$refs.editorStage');
      if (!editorStage) return;
      this.scrollPercentLeft = editorStage.scrollWidth > editorStage.clientWidth
        ? editorStage.scrollLeft / (editorStage.scrollWidth - editorStage.clientWidth)
        : this.initialScrollPercentLeft;
    },
    updateZoomScrollbars() {
      if (this.editorIsFullscreen) return;
      this.$nextTick(() => {
        const editorStage = get(this, '$refs.editorStage');
        if (!editorStage) return;
        if (editorStage.scrollHeight > editorStage.clientHeight) {
          editorStage.scrollTop = this.scrollPercentTop
            * (editorStage.scrollHeight - editorStage.clientHeight);
          this.suppressScrollEvent = true;
        } else {
          this.resetTopScrollPercent();
        }
        if (editorStage.scrollWidth > editorStage.clientWidth) {
          editorStage.scrollLeft = this.scrollPercentLeft
            * (editorStage.scrollWidth - editorStage.clientWidth);
          this.suppressScrollEvent = true;
        } else {
          this.resetLeftScrollPercent();
        }
      });
    },
    updateZoomSlideButtons() {
      /*
        Wait two cycles until after the scroll position has been
        set in updateZoomScrollbars() before determining the positions
        of the slide buttons since they are dependent on scroll position.
      */
      this.$nextTick(() => {
        this.$nextTick(() => {
          const {
            editorStage,
            zoomContent,
          } = this.$refs;
          if (!editorStage || !zoomContent) return;
          const stageRect = editorStage.getBoundingClientRect();
          const zoomRect = zoomContent.getBoundingClientRect();
          const firstLeft = `${this.slideButtonType === 'rectangle'
            ? zoomRect.left - this.horizontalCanvasPadding
            : zoomRect.left - this.horizontalCanvasPadding / 2}px`;
          const secondLeft = `${this.slideButtonType === 'rectangle'
            ? zoomRect.right + this.horizontalCanvasPadding
            : zoomRect.right + this.horizontalCanvasPadding / 2}px`;
          const firstXPct = this.slideButtonType === 'rectangle' ? '0%' : '-50%';
          const secondXPct = this.slideButtonType === 'rectangle' ? '-100%' : '-50%';
          const firstTransform = `translate(${firstXPct}, -50%)`;
          const secondTransform = `translate(${secondXPct}, -50%)`;
          const top = `${stageRect.top + stageRect.height / 2}px`;
          this.zoomPreviousButtonStyle = {
            top,
            left: this.isRtl ? secondLeft : firstLeft,
            transform: this.isRtl ? secondTransform : firstTransform,
          };
          this.zoomNextButtonStyle = {
            top,
            left: this.isRtl ? firstLeft : secondLeft,
            transform: this.isRtl ? firstTransform : secondTransform,
          };
        });
      });
    },
    transformZoomContent() {
      if (!this.$refs?.editorStage) return;
      const { editorStage } = this.$refs;

      if (this.editorIsFullscreen) {
        const scaledWidth = this.scale * EDITOR_CANVAS_WIDTH;
        const scaledHeight = this.isSlidesLayout
          ? this.scale * EDITOR_CANVAS_HEIGHT
          : this.scale * this.layoutHeight;
        const zoomTranslateX = this.isSlidesLayout
          ? (editorStage.clientWidth - scaledWidth) / 2
          : 0;
        const zoomTranslateY = scaledHeight >= editorStage.clientHeight
          ? 0 : (editorStage.clientHeight - scaledHeight) / 2;
        this.zoomContentStyle = {
          height: this.isSlidesLayout ? undefined : `${this.layoutHeight}px`,
          transform: `translate(${zoomTranslateX}px, ${zoomTranslateY}px) scale(${this.scale})`,
        };
        this.zoomSpacerStyle = { display: 'none' };
        return;
      }

      const scaledWidth = this.scaleZoomed * (EDITOR_CANVAS_WIDTH + this.canvasBorderWidth * 2);
      const scaledHeight = this.isSlidesLayout
        ? this.scaleZoomed * (EDITOR_CANVAS_HEIGHT + this.canvasBorderWidth * 2)
        : this.scaleZoomed * (this.layoutHeight + this.canvasBorderWidth * 2);
      const editorStageWidth = editorStage.clientWidth;
      const editorStageHeight = editorStage.clientHeight;
      const zoomCentered = editorStageWidth > (scaledWidth + this.horizontalCanvasPadding * 2);
      const zoomCenteredVertical = this.isSlidesLayout
        ? editorStageHeight > (scaledHeight + this.zoomContentHeaderHeight)
        : editorStageHeight > (scaledHeight + this.verticalCanvasPadding + this.zoomContentHeaderHeight);
      const zoomTranslateX = zoomCentered
        ? (editorStageWidth - scaledWidth) / 2
        : this.horizontalCanvasPadding;
      const zoomTranslateY = this.isSlidesLayout && zoomCenteredVertical
        ? (editorStageHeight - scaledHeight) / 2 + this.zoomContentHeaderHeight / 2
        : this.verticalCanvasPadding + this.zoomContentHeaderHeight;
      this.zoomContentStyle = {
        height: this.isSlidesLayout ? undefined : `${this.layoutHeight}px`,
        transform: `translate(${zoomTranslateX}px, ${zoomTranslateY}px) scale(${this.scaleZoomed})`,
        border: this.canvasBorderWidth
          ? `${this.canvasBorderWidth}px ${this.canvasBorderStyle} ${this.canvasBorderColor}`
          : undefined,
        boxShadow: this.canvasShadow,
      };

      const spacerTranslateX = zoomCentered
        ? (editorStageWidth - scaledWidth) / 2 - this.horizontalCanvasPadding
        : 0;
      const spacerTranslateY = zoomTranslateY + scaledHeight;
      const spacerHeight = zoomCenteredVertical ? 0 : this.verticalCanvasPadding;
      this.zoomSpacerStyle = {
        height: `${spacerHeight}px`,
        width: `${scaledWidth + this.horizontalCanvasPadding * 2}px`,
        transform: `translate(${spacerTranslateX}px, ${spacerTranslateY}px)`,
      };

      this.roundNavigationButtons = (scaledWidth + this.horizontalCanvasPadding * 2)
        < editorStage.clientWidth;

      this.zoomContentHeaderStyle = {
        height: `${EDITOR_ZOOM_CONTENT_HEADER_HEIGHT}px`,
        width: `${scaledWidth}px`,
        transform: `translate(${zoomTranslateX}px, ${zoomTranslateY - this.zoomContentHeaderHeight}px)`,
      };

      this.updateZoomSlideButtons();
    },
    updateZoomContentDisplay() {
      this.transformZoomContent();
      this.updateZoomScrollbars();
    },
    onWheel(event) {
      if (this.editorIsFullscreen) return;
      if (this.$refs.zoom && event.ctrlKey) {
        event.preventDefault();
        this.$refs.zoom.onWheel(event);
      }
    },
    displayInvalidPlacementMessage(data) {
      this.invalidPlacementAssetData = data;
      this.invalidPlacementAttempted = true;

      /**
       * Have a longer timeout if there is asset data in the error message so user can
       * read it
       */
      const timeout = this.invalidPlacementAssetData ? 5000 : 3000;
      setTimeout(() => {
        this.invalidPlacementAttempted = false;
      }, timeout);
    },
    undo() {
      this[types.UNDO_DRAFT_EDIT]();
    },
    redo() {
      this[types.REDO_DRAFT_EDIT]();
    },
    onKeydown(event) {
      if (this.mode === 'EDIT' && !get(this, 'modals.length')) {
        /*
          Handle undo/redo keyboard commands while editing.
          When user is focused inside a text input, use brower's default
          undo/redo handling for text inputs instead of our custom undo/redo stack.
        */
        if (!isTextInput(event.target)) {
          // ctrl+z (PC) cmd+z (Mac)
          const isUndo = (event.ctrlKey && event.key === 'z')
            || (!event.shiftKey && event.metaKey && event.key === 'z');
          // ctrl+y (PC) cmd+shift+z (Mac)
          const isRedo = (event.ctrlKey && event.key === 'y')
            || (event.metaKey && event.shiftKey && event.key === 'z');

          if (this.canUndo && isUndo) {
            this[types.UNDO_DRAFT_EDIT]();
          }
          if (this.canRedo && isRedo) {
            this[types.REDO_DRAFT_EDIT]();
          }
        }

        /*
          Handle key commands to toggle view mode preview.
          ctrl+space (PC) cmd+space (Mac)
        */
        const isViewModePreviewToggle = (event.ctrlKey || event.metaKey) && event.key === ' ';
        if (isViewModePreviewToggle && !this.editorIsFullscreen) {
          event.preventDefault();
          this.toggleViewModePreview();
        }

        /*
          Handle key commands to toggle snap grid on and off.
          ctrl+g (PC) cmd+g (Mac)
        */
        const isSnapGridToggle = this.canvasMode === 'EDIT'
          && (event.ctrlKey || event.metaKey) && event.key === 'g';
        if (isSnapGridToggle && !this.editorIsFullscreen) {
          event.preventDefault();
          this.toggleSnapGrid();
        }
      }
    },
    onRequestImmersiveReader() {
      if (this.editorIsFullscreen) {
        this.launchImmersiveReaderFullscreen();
      } else {
        // Swallow errors since messaging is handled inside launchImmersiveReader()
        this.launchImmersiveReader().catch(() => {});
      }
    },
    launchImmersiveReaderFullscreen() {
      this.fullscreenImmersiveReaderVisible = true;
      this.fullscreenImmersiveReaderLoading = true;
      const onExit = () => {
        this.fullscreenImmersiveReaderVisible = false;
      };
      this.$nextTick(async () => {
        try {
          await this.launchImmersiveReader({
            allowFullscreen: false,
            parent: this.$refs.fullscreenImmersiveReader,
            onExit,
          });
        } catch (error) {
          onExit();
        } finally {
          this.fullscreenImmersiveReaderLoading = false;
        }
      });
    },
    setActiveCanvas(canvasName) {
      /*
        The activeCanvasName will update based on if the user clicks the wrappers for
        Editor or Teacher Notes. This will allow for the user to paste in either location.
        It's a Pinia action here since it's referenced in the editorPaste mixin rather
        than in this component.
      */
      this[types.SET_ACTIVE_CANVAS_NAME](canvasName);
    },
    toggleViewModePreview() {
      if (this.editorIsFullscreen) return;

      // Close any drawers that are currently open, except notes.
      ['addModule', 'customization', 'chat'].forEach((drawer) => {
        if (this.editorDrawers[drawer]) {
          this[types.TOGGLE_EDITOR_DRAWER]({ drawer });
        }
      });

      // Toggle view mode preview on and off.
      this[types.SET_EDITOR_VIEW_MODE_PREVIEW](!this.editorViewModePreview);

      // Ensure that the resize sensor is attached
      this.$nextTick(() => {
        this.createCanvasResizeObserver();
      });
    },
    toggleSnapGrid() {
      if (this.editorIsFullscreen) return;
      const snapGrid = this.editorSnapGrid ? null : {
        cellWidth: EDITOR_SNAP_GRID_CELL_WIDTH,
        cellHeight: EDITOR_SNAP_GRID_CELL_HEIGHT,
        cellLargeWidthCellCount: EDITOR_SNAP_GRID_CELL_LARGE_WIDTH,
        cellLargeHeightCellCount: EDITOR_SNAP_GRID_CELL_LARGE_HEIGHT,
        lineColor: nebulaTokens.color.platform.neutral['500'],
        showGrid: true,
        z: 99999,
      };
      this[types.SET_EDITOR_SNAP_GRID](snapGrid);
    },
    onModuleRotationStart() {
      this.moduleIsRotating = true;
    },
    onModuleRotationEnd() {
      this.moduleIsRotating = false;
    },
    setTextSelectToolsKey() {
      /*
        Set the key for the text select tools. If a valid key is set,
        then text select tools will be rendered, otherwise it will not.
      */
      this.textSelectToolsKey = this.currentPageTextSelectKey;
    },
  },
};
</script>

<style lang="stylus">
@import '../components/modules/styles/grid-canvas';
@import '../components/modules/styles/module-options';
@import '../components/modules/styles/animations';
@import '../styles/editor-drawer';
@import '../styles/editor-themes';

.editor {
  // Note: Background colors and patterns are handled in src/styles/editor-themes.styl
  align-items: stretch;
  display: flex;
  height: calc(100vh - 65px); // prevent overlap with navbar
  position: relative;

  .page-content--embedded & {
    height: 100vh;
  }

  .editor__stage {
    flex-grow: 1;
    height: 100%;
    /*
      Horizontal and vertical scrolling is needed for panning
      zoomed content so overflow should be 'auto' for both x and y.
    */
    overflow: auto;
    position: relative;

    /*
      Make components sticky within the editor stage area. Elements with this class
      will stay in a fixed position within the editor stage area regardless of the
      stage area's scroll position.
    */
    &-sticky {
      height: 0;
      inset-block-start: 0;
      inset-inline-start: 0;
      overflow: visible;
      position: sticky;
      width: 0;
      z-index: 5;

      &--block-start {
        inset-block-start: 0;

        &-3x {
          inset-block-start: $nebula-space-3x;
        }
      }

      &--inline-end {
        inset-inline-start: 100%;

        &-3x {
          inset-inline-start: "calc(100% - %s)" % $nebula-space-3x;
        }
      }

      &--inline-end,
      &--inline-end-3x {

        & > :first-child {
          transform: translateX(-100%);
        }

        &.editor__stage-sticky--block-center {
          & > :first-child {
            transform: translate(-100%, -50%);
          }
        }
      }

      &--block-center {
        inset-block-start: 50%;

        & > :first-child {
          transform: translateY(-50%);
        }
      }
    }
  }

  &--toolbar {
    .editor__stage {
      height: calc(100% - 30px);
    }
  }

  &--module-rotating {
    cursor: grabbing;
  }

  .editor__stage-outer-wrap {
    display: flex;
    flex-grow: 1;
    height: 100%;
    justify-content: center;
    position: relative;
  }

  .editor__stage-outer-wrap--mystery {
    background: $nebula-color-black;
    padding: 0 150px;
  }

  .editor__rotate-screen-message {
    align-items: center;
    background: $nebula-color-interface-blue-600;
    bottom: 0;
    color: $nebula-color-white;
    display: none;
    flex-direction: column;
    font-size: $nebula-font-size-body-1;
    justify-content: center;
    left: 0;
    margin: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 300; // above editor drawers
  }

  .editor__rotate-screen-image {
    background-image: url('../assets/rotate-screen.svg');
    background-repeat: no-repeat;
    background-size: 100%;
    margin: -18% 0 $nebula-space-3x 6%;
    width: 50%;
  }

  .editor__rotate-screen-image-spacer {
    padding-bottom: 97%;
  }

  .editor__close-rotate-message {
    position: absolute;
    right: $nebula-space-1x;
    top: $nebula-space-1x;

    svg {
      fill: $nebula-color-white;
    }
  }

  .editor__close-fullscreen {
    background: transparent;
    left: $nebula-space-2x;
    position: absolute;
    top: $nebula-space-2x;
    z-index: 500;

    svg {
      fill: $nebula-color-white;
      transition: transform .2s;
      &:hover {
        transform: scale(1.2);
      }
      height: 100px;
      width: 100px;
    }
  }

  .editor__zoom-content-header {
    height: $nebula-space-3x;
    left: 0;
    position: absolute;
    top: 0;
    transform-origin: 0 0;
    z-index: 1;
  }

  .editor__zoom-content {
    background-color: $nebula-color-white;
    border-radius: $nebula-border-radius-small;
    box-shadow: 0 0 1px rgba(0, 0, 0, 0.001); // fixes transition rending bug
    height: 544px;
    left: 0;
    position: absolute;
    top: 0;
    transform-origin: 0 0;
    width: 1000px;
    z-index: 1;

    &--transition {
      transition: height 200ms ease;
    }

    &--border {
      box-sizing: content-box;
    }

    &--view-mode {
      overflow: hidden;
    }

    &--mystery-science {
      border-radius: 0;

      .component-module-wrapper__module-contents,
      .component-module-wrapper,
      .canvas-background-image,
      .comet-thumbnail,
      .full-slide-asset {
        border-radius: 0;
      }
    }
  }

  .editor__zoom-spacer {
    box-shadow: 0 0 1px rgba(0, 0, 0, 0.001); // fixes transition rendering bug
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    transform-origin: 0 0;
    z-index: 0;
  }

  .editor__zoom-slide-button {
    box-shadow: 0 0 1px rgba(0, 0, 0, 0.001); // fixes transition rendering bug
    left: 0;
    position: fixed;
    top: 0;
    transform-origin: 0 0;
    z-index: 2;
  }

  .editor__toolbar {
    $toolbar-height = 30px;

    align-items: center;
    background: rgba($nebula-color-interface-blue-500, .8);
    bottom: 0;
    clip-path: inset(-48px 0 0 -24px); // prevent overflowing into chat, but allow tooltips
    display: flex;
    height: $toolbar-height;
    justify-content: space-between;
    position: absolute;
    width: 100%;
    z-index: 800;

    .editor__toolbar-item {
      align-items: center;
      display: flex;
      height: $toolbar-height;
      margin-inline-start: $nebula-space-1x;
      position: relative;

      .nebula-switch__text {
        color: $nebula-color-white;
        font-size: 14px; // Custom size for toolbar
      }

      .nebula-switch__visual {
        height: 20px;  // Custom height for toolbar
      }

      .nebula-title-tooltip__trigger {
        align-items: center;
        display: flex;
      }

      .nebula-switch__input {
        &:checked + .nebula-switch__visual {
          background: $nebula-color-platform-interactive-700;
        }
      }
    }

    .editor__toolbar-divider {
      border-inline-end: 1px solid rgba($nebula-color-interface-blue-300, .5);
      display: block;
      height: $toolbar-height;
      margin-inline-start: $nebula-space-1x;
      width: 0;
    }

    .editor__toolbar-left {
      align-items: center;
      display: flex;
      justify-content: flex-start;

      .editor__toolbar-item:first-child {
        margin-inline-start: 12px;
      }
    }

    .editor__toolbar-center {
      align-items: center;
      display: flex;
      flex: 1;
      justify-content: center;
    }

    .editor__toolbar-right {
      align-items: center;
      display: flex;
      justify-content: flex-end;
    }
  }

  &__toolbar-button.nebula-button--icon-only {
    background: transparent;
    padding: $nebula-space-half;

    &:hover, &:focus, &:active {
      background: transparent;
    }

    svg {
      fill: $nebula-color-white;
    }

    &[disabled],
    &[aria-disabled="true"] {
      svg {
        fill: $nebula-color-platform-neutral-400;
      }
    }
  }

  &__slide-button {
    align-items: center;
    display: flex;
    flex-direction: column;
    font-size: $nebula-font-size-caption;
    font-weight: bold;
    gap: $nebula-space-1x;
    justify-content: center;
    pointer-events: auto;
    transition: background $nebula-transition-default;

    &--rectangle {
      --nebula-button-background: $nebula-color-platform-neutral-300;
      --nebula-button-background-hover: $nebula-color-platform-neutral-500;
      --nebula-button-background-active: $nebula-color-platform-neutral-500;
      --nebula-button-background-disabled: $nebula-color-platform-neutral-200;
      --nebula-button-text-color: $nebula-color-platform-neutral-1000;
      --nebula-button-text-color-hover: $nebula-color-platform-neutral-1000;
      --nebula-button-text-color-active: $nebula-color-platform-neutral-1000;
      --nebula-button-text-color-disabled: rgba($nebula-color-platform-neutral-1000, 0.5);

      height: 110px;
      padding: 2px;
      width: 43px;

      &.editor__slide-button--prev,
      &.editor__slide-button--next, {
        &.editor__slide-button--rounded {
          border-radius: $nebula-border-radius-small;
        }
      }

      &.editor__slide-button--prev {
        border-radius: 0 $nebula-border-radius-small $nebula-border-radius-small 0;
      }

      &.editor__slide-button--next {
        border-radius: $nebula-border-radius-small 0 0 $nebula-border-radius-small;
      }

      .nebula-button__icon {
        height: $nebula-space-3x;
        width: $nebula-space-3x;
      }

      .nebula-button__text {
        margin: 0;
        overflow-wrap: break-word;
        width: 100%;
      }
    }

    &--circle {
      --nebula-button-background: $nebula-color-platform-white;
      --nebula-button-background-hover: $nebula-color-platform-interface-900;
      --nebula-button-background-active: $nebula-color-platform-interface-1000;
      --nebula-button-background-disabled: $nebula-color-platform-white;
      --nebula-button-text-color: $nebula-color-platform-interface-1000;
      --nebula-button-text-color-hover: $nebula-color-platform-white;
      --nebula-button-text-color-active: $nebula-color-platform-white;
      --nebula-button-text-color-disabled: rgba($nebula-color-platform-interface-1000, 0.3);
      --nebula-button-border: 1px solid;
      --nebula-button-border-color: $nebula-color-platform-interface-1000;
      --nebula-button-border-color-hover: $nebula-color-platform-white;
      --nebula-button-border-color-active: $nebula-color-platform-white;
      --nebula-button-border-color-disabled: rgba($nebula-color-platform-interface-1000, 0.3);
    }
  }

  &--is-fullscreen {
    .editor__stage {
      height: 100%;

      &--landscape-only-fullscreen {
        @media screen and (orientation: portrait) {
          .editor__rotate-screen-message {
            display: flex;
          }

          .editor__zoom-content,
          .editor__zoom-spacer,
          .editor__fullscreen-header {
            display: none;
          }
        }
      }
    }

    .editor__zoom-content {
      border-radius: 0;
    }

    .editor__no-pages-visible-fullscreen-message {
      align-items: center;
      background: $nebula-color-neutral-100;
      bottom: 0;
      display: flex;
      justify-content: center;
      left: 0;
      margin: 0;
      position: fixed;
      right: 0;
      top: 0;
      z-index: 300; // above editor drawers
    }

    .notes__contents,
    .editor__highlight-notes-drawer {
      margin-top: $nebula-space-7x;
    }

    .editor-floating-buttons {
      display: none;
    }
  }

  &--is-fullscreen-slides {
    .editor__stage {
      overflow: hidden;
    }
  }

  &--is-fullscreen-board {
    .editor__stage {
      overflow-x: hidden;
      overflow-y: auto;
    }
  }

  &--is-modal-active {
    .editor__stage {
      overflow: hidden;
    }
  }

  .zoom-control {
    one-hundred-mark = @block {
      background-position: 47.5%;
      background-size: 5px 8px;
      background-repeat: no-repeat;
      background-image: url('~@/assets/black-background.svg');
    }

    .zoom-control__slider::-webkit-slider-runnable-track {
      {one-hundred-mark}
    }

    .zoom-control__slider::-moz-range-track {
      {one-hundred-mark}
      height: 8px;
    }
  }

  &__invalid-placement-message {
    position: absolute;
    top: 0;
    z-index: 151;

    .comet-block-message__list {
      margin: $nebula-space-1x 0 0 0;
    }
  }

  &__fullscreen-immersive-reader {
    background: $nebula-color-white;
    bottom: 0;
    display: none;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1000;

    &--visible {
      display: block;
    }
  }

  &__immersive-reader {
    position: absolute;
    inset-inline-end: 0;
    top: 80px;
    z-index: 200;

    &-transition--enter-active {
      transition: all 0.25s ease-in-out;
    }
    &-transition--leave-active {
      transition: all 0.25s ease-in-out;
    }
    &-transition--enter-from,
    &-transition--leave-to {
      right: -50px;
    }

    &--student-view {
      top: $nebula-space-3x;
    }
  }

  &__drawer-buttons {
    overflow: visible;
    position: relative;
    width: 0;
    z-index: 200;
  }

  &__drawer-button-wrapper {
    display: block;
    position: relative;
    width: 52px;

    &--right {
      transform: translateX(-100%);
    }
  }

  &__drawer-button.comet-button {
    border-radius: 0 $nebula-border-radius-badge-default $nebula-border-radius-badge-default 0;
    display: block;
    margin-top: $nebula-space-2x;

    &.editor__drawer-button--right {
      border-radius: $nebula-border-radius-badge-default 0 0 $nebula-border-radius-badge-default;
    }
  }

  .editor__text-select-tools {
    position: fixed;
  }

  .notes__contents {
    position: relative;
    z-index: 200; // in front of the main canvas

    &-transition--enter-active {
      transition: all 0.25s ease-in-out;
    }
    &-transition--leave-active {
      transition: all 0.25s ease-in-out;
    }
    &-transition--enter-from,
    &-transition--leave-to {
      left: -100px;
    }
  }

  .notes__contents .add-module__container {
    bottom: 0;
    display: flex;
    left: 0;
    justify-content: center;
    position: absolute;
    transition: left 0.25s ease-in-out;
    width: 100%;
    z-index: 100;

    &-transition--enter-active {
      transition: all 0.05s ease-in-out;
    }
    &-transition--leave-active {
      transition: all .5s ease-in-out;
    }
    &-transition--enter-from,
    &-transition--leave-to {
      left: -150px;
    }
  }

  .notes__contents .notes-canvas__wrapper--open + .add-module__container {
    left: 0;
  }

  &__modal-overlay {
    // Match nebula-modal-overlay styles
    background: rgba(40, 57, 62, .85);
    backdrop-filter: blur(15px);
    height: 100%;
    inset-block-end: 0;
    inset-block-start: 0;
    inset-inline-end: 0;
    inset-inline-start: 0;
    position: sticky; // even if we scroll around in the main canvas, this should stay put
    width: 100%;
    z-index: 797;
  }

  html[dir="rtl"] & {
    .zoom-control {
      .zoom-control__slider::-webkit-slider-runnable-track {
        background-position: 52.5%;
      }

      .zoom-control__slider::-moz-range-track {
        background-position: 52.5%;
      }
    }

    &__drawer-button-wrapper--right {
      transform: translateX(100%);
    }

    &__drawer-button.comet-button {
      border-radius: $nebula-border-radius-badge-default 0 0 $nebula-border-radius-badge-default;
      margin-right: 0;

      &.editor__drawer-button--right {
        border-radius: 0 $nebula-border-radius-badge-default $nebula-border-radius-badge-default 0;
      }
    }

    .editor__slide-button {
      &--rectangle {
        &.editor__slide-button--prev,
        &.editor__slide-button--next, {
          &.editor__slide-button--rounded {
            border-radius: $nebula-border-radius-small;
          }
        }

        &.editor__slide-button--prev {
          border-radius: $nebula-border-radius-small 0 0 $nebula-border-radius-small;
        }

        &.editor__slide-button--next {
          border-radius: 0 $nebula-border-radius-small $nebula-border-radius-small 0;
        }
      }
    }

    .editor__toolbar-item svg {
      transform: scaleX(-1);
    }

    .editor__stage {
      &-sticky {
        &--inline-end,
        &--inline-end-3x {

          & > :first-child {
            transform: translateX(100%);
          }

          &.editor__stage-sticky--block-center {
            & > :first-child {
              transform: translate(100%, -50%);
            }
          }
        }
      }
    }
  }
}
</style>
